import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import DrawRectangle from 'mapbox-gl-draw-rectangle-mode';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import percentile from 'percentile';
import { Sidebar } from 'primereact/sidebar';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { Toast } from 'primereact/toast';
import { toast, registerToast } from './toastService';
import { POINT_OUTSIDE_OPACITY, CLUSTER_OUTSIDE_OPACITY, INSIDE_CLUSTER_PROPERTIES, markFeaturesInsideShapes } from './insideShape';

import FormFiltersContainer from './FormFiltersContainer';
import Legenda from '../Legenda';
import HeatMapLegend from '../HeatMapLegend';
import HeatMapModal from './HeatMapModal';
import HeatMapControls from './HeatMapControls';
import LegendaPoloGerador from '../LegendaPoloGerador';
import PolosGeradoresTypesModal from './PolosGeradoresTypesModal';
import PolosDaAreaModal from './PolosDaAreaModal';
import ModalDashboard from '../ModalDashboard';
import MapboxResultsPanel from './MapboxResultsPanel';
import CompareModal from './CompareModal';
import EmpreendimentoModal from './EmpreendimentoModal';
import RaioDeslocamentoModal from './RaioDeslocamentoModal';
import ModalRanges from './ModalRanges';
import ModalSalvarProjeto from '../Projeto/ModalSalvarProjeto';
import MapCapture from '../MapCapture/MapCapture';
import DrawnShapesList from './DrawnShapesList';
import ShapeAttributesEditor from './ShapeAttributesEditor';
import { circleTwoClickMode, markerCircleMode, DrawExtraControls, makeCircleFeature, drawStyles, restoreDragPan } from './drawModes';
import { RulerTool, RULER_LAYERS } from './rulerTool';
import geolocationService from '../../services/geolocationService';
import KmlService from '../../services/KmlService';
import FiltroZoneamento from '../FiltroZoneamento';
import LegendaDataLayer from '../LegendaDatalayer/Legenda';
// Dados censitários (camadas Linkages/IBGE) — paridade com o mapa Google (DefaulMap.jsx).
import LinkagesDataLayerService from '../../services/LinkagesDataLayer/LinkagesDataLayerService';
import LinkagesDataDictionaryModal from '../LinkagesDataModal/LinkagesDataDictionaryModal';
import LinkagesDataTableModal from '../LinkagesDataModal/LinkagesDataTableModal';
import RendaIbgeLegend from '../RendaIbgeLegend';
import ModalLegendaRendaIbge from '../ModalLegendaRendaIbge/ModalLegendaRendaIbge';
import colors, { polosGeradoresColor } from '../../util/colors';
import LegendaGradientePolos from './LegendaGradientePolos';
// Features compartilhadas com os demais mapas Mapbox (Revenda e Locação, etc.).
import { parseCidadeValue } from './features/cidade';
import { LAYER_LOADING_INITIAL_STATE } from './features/layerLoading';
import LayerLoadingIndicator from './features/LayerLoadingIndicator';
import * as zoneamento from './features/zoneamentoFeature';
import * as datalayer from './features/datalayer/datalayerFeature';
import SociodemografiaTools from './features/datalayer/SociodemografiaTools';
import * as mapCapture from './features/mapCapture';
import * as isochrone from './features/isochroneFeature';
import { syncAddressMarker, removeAddressMarker } from './features/addressMarker';
import '../mapa.css';

const BRAZIL_CENTER = [-51.9253, -14.235];
const DEFAULT_ZOOM = 4;
const SELECTED_ZOOM = 12;
const SOURCE_ID = 'empreendimentos';
const BAIRROS_SOURCE_ID = 'bairros';
const HEAT_SOURCE_ID = 'empreendimentos-heat';
const EMPTY_FC = { type: 'FeatureCollection', features: [] };

// Projetos salvos no mapa Google guardam o status na nomenclatura do MongoDB, onde
// 'Comercialização' é o 'Ativo' do MySQL — sem traduzir, a busca do projeto carregado
// não devolve nenhum empreendimento (ver FormFiltersContainer.STATUS_OPTIONS).
const STATUS_LEGADO = { 'Comercialização': 'Ativo' };

// O switch de quartos "Studio" (FormFilters.dormitorios) viaja como o id de `dormitories`
// que typologies.number_bedroom guarda para "1 dorm - Studio".
const DORMITORIO_STUDIO = 5;

// Polos geradores — paridade com o mapa Google. main_category → arquivo de ícone
// (espelha PoloGerador::getImageAttribute e LegendaPoloGerador). Imagens carregadas
// sob o nome `polo_<arquivo>` em /img/polos_geradores/grupos/<arquivo>.png.
const POLO_ICON_BY_CATEGORY = {
    'Alimentação': 'alimentacao',
    'Automotivo': 'automotivo',
    'Decoração': 'decoracao',
    'Educação': 'educacao',
    'Instituição Financeira': 'financeira',
    'Moda': 'moda',
    'Outros': 'outros',
    'Saúde e Bem-Estar': 'saude',
    'Serviços': 'servicos',
    'Shopping Center': 'shopping-center-2',
    'Loja De Materiais De Construção': 'construcao-e-bricolagem',
};
const POLO_ICON_FILES = [...new Set(Object.values(POLO_ICON_BY_CATEGORY))];
const POLO_CATEGORIES = Object.keys(POLO_ICON_BY_CATEGORY);
// Balde das categorias que não constam do mapeamento acima (ex.: 'Centros Comerciais',
// que existe em PoloGerador::GROUPS mas não nas legendas) — mesmo destino do ícone de
// fallback que o backend já aplica.
const POLO_CATEGORY_FALLBACK = 'Outros';

/** Escapa texto vindo do banco antes de concatenar no HTML dos popups. */
const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);

/** Preço em reais sem centavos — formato usado no popup do marcador. */
const BRL_SEM_CENTAVOS = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

/** Categoria de polo → sufixo usável em id de source/camada ('Alimentação' → 'alimentacao'). */
const poloSlug = (category) =>
    category
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

const POLO_SLUG_BY_CATEGORY = Object.fromEntries(POLO_CATEGORIES.map((c) => [c, poloSlug(c)]));

const polosSourceId = (category) => `polos-geradores-${POLO_SLUG_BY_CATEGORY[category]}`;
const polosClusterLayerId = (category) => `polos-clusters-${POLO_SLUG_BY_CATEGORY[category]}`;
const polosCountLayerId = (category) => `polos-cluster-count-${POLO_SLUG_BY_CATEGORY[category]}`;
const polosPointLayerId = (category) => `polos-unclustered-point-${POLO_SLUG_BY_CATEGORY[category]}`;

/*
 * Uma source clusterizada por categoria, e não uma só para todos os polos: no mapa Google
 * cada categoria tem o seu próprio MarkerClusterer (DefaulMap.jsx), então nenhum cluster
 * mistura categorias e cada bolha pode receber a cor da sua. Com source única o MapBox
 * fundiria categorias diferentes na mesma bolha e não haveria cor honesta para ela — a
 * legenda de gradiente (LegendaGradientePolos) estaria mentindo.
 */
const POLOS_LAYERS = POLO_CATEGORIES.flatMap((category) => [
    polosClusterLayerId(category),
    polosCountLayerId(category),
    polosPointLayerId(category),
]);

// Mapa de calor — paridade com o mapa Google (DefaulMap.jsx).
// As 8 cores/buckets de percentil e os PNGs em /img/marcadores/mapa_de_calor/.
const HEAT_COLORS = ['194D72', '3D8ABD', 'B1D974', '7CA83E', '5B7537', 'F1A30C', 'B29400', 'C50234'];
const HEAT_ICON_NAMES = HEAT_COLORS.map((hex) => `heat_${hex}`);
const HEAT_PERCENTILES = [12.5, 25, 32.5, 50, 62.5, 75, 87.5];
// Camadas que compõem a exibição padrão de empreendimentos (clusters + marcadores).
const CLUSTER_LAYERS = ['clusters', 'cluster-count', 'unclustered-point'];
// range.type (ModalRanges) → property correspondente no GeoJSON de empreendimentos.
const RANGE_PROPERTY = {
    area_media: 'area_media',
    ticket_medio_metro: 'ticket_medio_metro',
    total_unidades: 'oferta_inicial',
    oferta_final: 'oferta_final',
};
const HEAT_LAYERS = ['heatmap-layer', 'heat-points'];

/*
 * Escalas de cor do mapa de calor (`heatmap-color`), conforme aprovado internamente.
 * A parada em 0 é sempre transparente: é o que deixa o basemap aparecer fora das
 * concentrações, em vez de o mapa inteiro ganhar um véu colorido.
 */
const HEAT_COLOR_RAMPS = {
    hot: ['interpolate', ['linear'], ['heatmap-density'],
        0, 'rgba(49,54,149,0)', 0.2, 'rgba(69,117,180,0.6)',
        0.6, 'rgba(253,174,97,0.9)', 1, 'rgba(215,48,39,1)'],
    cool: ['interpolate', ['linear'], ['heatmap-density'],
        0, 'rgba(0,0,80,0)', 0.3, 'rgba(0,70,200,0.6)',
        0.7, 'rgba(100,200,255,0.9)', 1, 'rgba(200,240,255,1)'],
    density: ['interpolate', ['linear'], ['heatmap-density'],
        0, 'rgba(45,0,75,0)', 0.3, 'rgba(115,0,211,0.5)',
        0.7, 'rgba(224,64,251,0.8)', 1, 'rgba(255,255,255,1)'],
    profit: ['interpolate', ['linear'], ['heatmap-density'],
        0, 'rgba(0,60,0,0)', 0.3, 'rgba(0,102,0,0.5)',
        0.7, 'rgba(136,204,0,0.8)', 1, 'rgba(255,238,0,1)'],
};

// Ajustes finos do mapa de calor. Os valores default são os do exemplo aprovado.
const HEAT_STYLE_DEFAULT = { ramp: 'hot', radius: 25, intensity: 1, opacity: 0.75 };
// Cidades com limites de bairros disponíveis (formato "UF-Cidade"), injetadas no
// page load a partir da tabela cities. Decide a exibição do switch sem AJAX.
const BAIRROS_CITIES = new Set(window.bairrosCities || []);

// Zoneamento e Dados censitários vivem em ./features/, compartilhados com os demais
// mapas Mapbox; a permissão `ver-zoneamento` é injetada no page load pela blade.
const CAN_VER_ZONEAMENTO = zoneamento.CAN_VER_ZONEAMENTO;

const MAP_STYLE_STORAGE_KEY = 'geobrain.mapbox.style';
/*
 * A chave `light` é o mapa de ruas padrão e vem de antes de existir a opção `claro`
 * (light-v11, o estilo pálido). Os nomes se parecem, mas são estilos diferentes —
 * `light` não foi renomeada porque é o valor já gravado no localStorage dos usuários.
 */
const MAP_STYLES = {
    light:     'mapbox://styles/mapbox/streets-v12',
    dark:      'mapbox://styles/mapbox/dark-v11',
    satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
    claro:     'mapbox://styles/mapbox/light-v11',
};
const MAP_STYLE_OPTIONS = [
    { value: 'light',     label: 'Ruas' },
    { value: 'claro',     label: 'Claro' },
    { value: 'dark',      label: 'Escuro' },
    { value: 'satellite', label: 'Satélite' },
];
const DEFAULT_MAP_STYLE = 'light';
const readStoredMapStyle = () => {
    try {
        const v = localStorage.getItem(MAP_STYLE_STORAGE_KEY);
        return MAP_STYLES[v] ? v : DEFAULT_MAP_STYLE;
    } catch {
        return DEFAULT_MAP_STYLE;
    }
};

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const GOOGLE_SCRIPT_ID = 'google-maps-places-script';

// H/C/W: Horizontal, Comercial e Hotel (o resto são as letras de padrão do Vertical).
const ICON_LETTERS = ['E', 'O', 'S', 'M', 'A', 'L', 'X', 'H', 'C', 'W'];
const ICON_NAMES = [
    ...ICON_LETTERS.flatMap((l) => [`green_Marker${l}`, `red_Marker${l}`]),
    'yellow_MarkerF',
];

/**
 * Escala dos marcadores de empreendimento e do mapa de calor. Os PNGs têm 20x34
 * (calor: 20x20), então 0.85 desenha o pin com ~17x29 — perto do tamanho nativo, sem
 * borrar. Vale para as duas camadas de marcador e, junto, para os pins de range
 * (createRangePinImage), preservando a proporção entre eles.
 */
const MARKER_ICON_SIZE = 0.85;

function loadGoogleMapsPlaces() {
    if (window.google?.maps?.places) return Promise.resolve();
    if (document.getElementById(GOOGLE_SCRIPT_ID)) {
        return new Promise((resolve) => {
            document.getElementById(GOOGLE_SCRIPT_ID).addEventListener('load', resolve, { once: true });
        });
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = GOOGLE_SCRIPT_ID;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&language=pt-BR&region=BR`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

class MapboxMap extends Component {
    state = {
        left_side_bar_visible: true,
        right_panel_visible: false,
        empreendimentoDetail: null,
        showEmpreendimentoModal: false,
        loadingEmpreendimentoDetail: false,
        active_tab: 'filtros',
        totalEmpreendimentos: 0,
        // Total carregado na busca (inclui os que ficaram fora das formas desenhadas).
        totalCarregados: 0,
        cidadeSincronizada: null,
        projetos: [{ value: null, label: 'Selecione' }],
        mapStyle: readStoredMapStyle(),
        searchAreas: [],
        showBairros: false,
        bairrosAvailable: false,
        ...mapCapture.MAP_CAPTURE_INITIAL_STATE,
        drawnShapes: [],
        modalHeatMapOpen: false,
        showHeatMap: false,
        heatMapWeightType: null,
        heatMapLegendConfig: [],
        // Paleta e ajustes finos do calor — aplicados ao vivo, sem recalcular percentis.
        heatMapStyle: { ...HEAT_STYLE_DEFAULT },
        shapeEditorOpen: false,
        shapeEditing: null,
        showPolosGeradores: false,
        polosModalOpen: false,
        polosTypesSelected: [],
        polosLoaded: false,
        polosLoading: false,
        // Categorias com polos de fato no mapa — alimenta a legenda flutuante de gradiente.
        polosCategoriasCarregadas: [],
        // Botões POLOS e DASHBOARD do card de área (paridade com o InfoCard do mapa Google).
        polosDaAreaModal: null,
        dashboardUrl: null,
        gerandoDashboard: false,
        showEmpreendimentos: true,
        // Agrupamento dos marcadores em clusters — ligado por padrão, como no mapa Google.
        clusterMarkers: true,
        // "Incluir cidades não monitoradas" (aba Ferramentas, só usuários internos):
        // por padrão a busca cobre apenas cidades monitoradas (paridade com o mapa Google).
        incluirNaoMonitoradas: false,
        // Comparar Áreas / Comparar Polos (XLSX de comparação entre áreas analisadas).
        showCompareAreasModal: false,
        showComparePolosModal: false,
        ...zoneamento.ZONEAMENTO_INITIAL_STATE,
        // Dados censitários (camadas Linkages/IBGE).
        ...datalayer.DATALAYER_INITIAL_STATE,
        // Rótulos das camadas em carregamento (zoneamento, dados censitários…):
        // alimenta o indicador flutuante sobre o mapa.
        ...LAYER_LOADING_INITIAL_STATE,
        loadingKml: false,
        // Raio por Deslocamento (isócrona) e régua.
        ...isochrone.ISOCHRONE_INITIAL_STATE,
        // Ranges customizados de cor/imagem dos marcadores (ModalRanges)
        ranges: [],
        modalRangesOpen: false,
        // Projetos (salvar / carregar / excluir / compartilhar)
        modalSalvarProjetoOpen: false,
        projectName: '',
        projectShareUsers: [],
        projectErrors: {},
        savingProject: false,
        loadedProjectId: null,
    };

    // Input de arquivo (oculto) para importação de KML/KMZ.
    kmlInputRef = React.createRef();

    // Última config de mapa de calor escolhida no modal (para reaplicar após
    // troca de filtros/estilo). null quando o heatmap está desligado.
    heatMapConfig = null;

    // Cache cru das features de polos geradores (antes do recorte por forma).
    allPolos = [];

    // Assinatura das formas desenhadas usadas na última busca de polos (ver
    // refreshPolosPorArea).
    _polosShapesKey = '';

    // Sequencial das buscas de polos — descarta respostas fora de ordem.
    _polosRequestId = 0;

    bairrosCache = {};

    containerRef = React.createRef();
    searchInputRef = React.createRef();
    formFiltersRef = React.createRef();
    toastRef = React.createRef();
    map = null;
    draw = null;
    drawExtras = null;
    currentGeojson = null;
    // Marcador do endereço buscado (só para ponto específico, não para cidade).
    addressMarker = null;

    allFeatures = [];
    // Recorte de allFeatures pelas formas desenhadas — sem formas, é a lista inteira.
    // As demais features continuam no mapa (esmaecidas), mas fora das análises.
    insideFeatures = [];
    // Filtros da última busca — usados quando a sidebar (e com ela o FormFilters) está fechada.
    ultimosFiltros = null;

    componentDidMount() {
        // Disponibiliza o Toast do PrimeReact para todo o mapa (via toastService).
        registerToast(this.toastRef);

        // Campos de instância das features compartilhadas (serviço de zoneamento,
        // caches das camadas). Ver ./features/.
        zoneamento.initZoneamento(this);
        datalayer.initDatalayer(this);

        const apiKey = window.mapboxApiKey;
        if (!apiKey || !this.containerRef.current) {
            if (!apiKey) console.error('[MapboxMap] window.mapboxApiKey ausente');
            return;
        }

        mapboxgl.accessToken = apiKey;
        this.map = new mapboxgl.Map({
            container: this.containerRef.current,
            style: MAP_STYLES[this.state.mapStyle],
            center: BRAZIL_CENTER,
            zoom: DEFAULT_ZOOM,
            preserveDrawingBuffer: true, // necessário para getCanvas().toDataURL() na captura de tela
        });

        this.map.on('error', (e) => console.error('[MapboxMap] erro:', e.error));

        this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right');
        this.map.addControl(
            new mapboxgl.GeolocateControl({
                positionOptions: { enableHighAccuracy: true },
                trackUserLocation: true,
            }),
            'top-right'
        );

        this.setupDrawTools();

        this.map.on('load', () => this.initializeLayers());

        this.setupGooglePlaces();
        this.loadSociodemografiaPermissions();
        this.loadProjetos();
    }

    /**
     * Lista do combo "Carregar projeto". Fica aqui, e não no FormFiltersContainer, porque
     * a Sidebar do PrimeReact desmonta o conteúdo ao fechar (CSSTransition unmountOnExit)
     * e o LIMPAR reseta o estado do formulário — nos dois casos a lista sumia do combo.
     */
    loadProjetos = async () => {
        try {
            const response = await axios.get(route('getMapaProjetos'));
            this.setState({ projetos: [{ value: null, label: 'Selecione' }, ...response.data.projects] });
        } catch (e) {
            console.error('[MapboxMap] erro ao carregar projetos:', e);
            toast.error('Não foi possível carregar a lista de projetos.');
        }
    };

    setupDrawTools = () => {
        this.draw = new MapboxDraw({
            displayControlsDefault: false,
            // Sem controles nativos: o polígono fica junto dos demais botões de
            // desenho (DrawExtraControls) e a exclusão é feita pela lista de formas.
            controls: {},
            // Necessário para os estilos lerem os atributos da feature via `user_*`
            // (cor/opacidade/borda por forma — ver drawStyles).
            userProperties: true,
            styles: drawStyles,
            modes: {
                ...MapboxDraw.modes,
                draw_rectangle: DrawRectangle,
                draw_circle_two_click: circleTwoClickMode,
                draw_marker_circle: markerCircleMode,
            },
        });
        // Ferramentas de desenho no topo, à esquerda do campo de busca (em 'top-right'
        // ficavam escondidas sob os controles de navegação); posição via CSS do blade.
        this.map.addControl(this.draw, 'top-left');

        // A régua se desliga sozinha no Esc, então o realce do botão vem do callback
        // e não do ponto onde ela foi ligada.
        this.ruler = new RulerTool({
            map: this.map,
            onChange: ({ active }) => this.drawExtras?.setRulerActive(active),
        });

        this.drawExtras = new DrawExtraControls({
            draw: this.draw,
            includePolygon: true,
            onIsochronePick: this.toggleIsochronePick,
            onRuler: this.toggleRuler,
            onModeChange: this.handleDrawModeChange,
        });
        this.map.addControl(this.drawExtras, 'top-left');

        this.map.on('draw.create', this.handleDrawCreate);
        this.map.on('draw.update', this.handleDrawUpdate);
        this.map.on('draw.delete', this.handleDrawDelete);

        /*
         * Os dois caminhos de troca de modo, que se complementam:
         *  - acionar uma ferramenta passa por DrawExtraControls.onModeChange, porque
         *    `draw.changeMode()` é API pública e roda silenciosa (suppressAPIEvents);
         *  - sair do modo (desenho concluído, Esc) é troca interna da lib e chega aqui.
         */
        this.map.on('draw.modechange', ({ mode }) => this.handleDrawModeChange(mode));

        // Rede de segurança: os eventos marcam o fim de uma interação com as formas,
        // então é seguro devolver o arrasto ao mapa (ver restoreDragPan).
        ['draw.create', 'draw.update', 'draw.delete', 'draw.modechange'].forEach(
            (evento) => this.map.on(evento, this.restoreDragPan)
        );
    };

    /**
     * Reage à troca de modo de desenho: desenhar e medir são exclusivos, então entrar
     * num modo de desenho encerra a régua.
     *
     * @param {string|undefined} mode Modo do mapbox-gl-draw.
     */
    handleDrawModeChange = (mode) => {
        if (mode && mode !== 'simple_select') this.deactivateRuler();
    };

    applyShapeFilter = () => {
        if (!this.map?.getSource(SOURCE_ID)) return;

        const shapes = (this.draw?.getAll().features || []).filter(
            (f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
        );

        // Os empreendimentos fora das formas continuam no mapa (esmaecidos pela camada,
        // via `inside`) para o usuário enxergar onde vale a pena desenhar a próxima área.
        const dentro = markFeaturesInsideShapes(this.allFeatures, shapes);

        this.insideFeatures = dentro;
        const geojson = { type: 'FeatureCollection', features: this.allFeatures };
        this.currentGeojson = geojson;
        this.map.getSource(SOURCE_ID).setData(geojson);
        this.setState({ totalEmpreendimentos: dentro.length, totalCarregados: this.allFeatures.length });

        // Se o mapa de calor está ativo, re-deriva a camada de calor a partir dos
        // empreendimentos atualmente filtrados.
        if (this.state.showHeatMap && this.heatMapConfig) {
            this.addHeatMap(this.heatMapConfig);
        }

        // Marca os polos geradores pelas mesmas formas desenhadas.
        if (this.state.showPolosGeradores && this.allPolos.length) {
            this.applyPolosShapeFilter();
            // E refaz a busca se as formas mudaram: é do recorte geográfico que vêm os
            // polos fora das cidades selecionadas (vizinhos de divisa, `city` divergente).
            this.refreshPolosPorArea();
        }
    };

    handleDrawCreate = async (e) => {
        // A busca disparada no fim deste handler já recarrega os polos com a forma nova;
        // pré-grava a assinatura para applyShapeFilter não pedir a mesma coisa duas vezes.
        this._polosShapesKey = JSON.stringify(this.polosPolygonsDoDraw());
        this.applyShapeFilter();

        const newShape = e.features?.[0];
        if (!newShape || (newShape.geometry?.type !== 'Polygon' && newShape.geometry?.type !== 'MultiPolygon')) return;

        const shapeType = newShape.properties?.shapeType;
        const typeLabels = { circle: 'Círculo', marker_circle: 'Círculo', rectangle: 'Retângulo' };
        const type = typeLabels[shapeType] || 'Polígono';
        const name = `${type} ${this.state.drawnShapes.length + 1}`;
        const color = colors[this.state.drawnShapes.length % colors.length];
        const areaId = `drawn-${Date.now()}`;

        // Atributos de estilo padrão; ficam também nas properties da feature, de
        // onde o tema data-driven do draw (drawStyles) os lê via `user_*`.
        const styleAttrs = { fillColor: color, fillOpacity: 0.5, strokeColor: '#000000', strokeWeight: 3 };
        Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(newShape.id, k, v));

        // Registra a forma na lista (independente de conter empreendimentos). O await é
        // necessário: é de this.state.drawnShapes que saem os bounding boxes do payload
        // e as formas reanalisadas pela busca disparada logo abaixo.
        await new Promise((resolve) => this.setState((prev) => ({
            drawnShapes: [...prev.drawnShapes, {
                drawId: newShape.id,
                areaId,
                type,
                name,
                color,
                visible: true,
                feature: this.draw.get(newShape.id) || newShape,
                center: newShape.properties?.center || null,
                radiusMeters: newShape.properties?.radiusMeters || null,
                ...styleAttrs,
            }],
        }), resolve));

        await this.buscarPelaAreaDesenhada();
    };

    /**
     * Busca disparada pelo próprio desenho: terminar a forma já carrega os
     * empreendimentos da área, sem depender do botão BUSCAR. Reusa os filtros do
     * formulário — e os da última busca quando a sidebar está fechada, porque o
     * PrimeReact desmonta o conteúdo dela (e com ele o estado do FormFilters).
     */
    buscarPelaAreaDesenhada = () =>
        this.handleFiltersSubmit(this.formFiltersRef.current?.getFilters?.() || this.ultimosFiltros || {});

    handleDrawUpdate = (e) => {
        const updatedFeatures = e.features || [];

        updatedFeatures.forEach((feat) => {
            // O draw move as coordenadas, mas não o `center` guardado nas properties do
            // círculo — e é dele que dependem o ajuste de raio, a exportação KML e o
            // salvamento do projeto. Recalcula a partir da geometria já movida.
            const isCircle = ['circle', 'marker_circle'].includes(feat.properties?.shapeType);
            const novoCentro = isCircle ? this.centroDaGeometria(feat.geometry) : null;
            if (novoCentro) {
                this.draw.setFeatureProperty(feat.id, 'center', [novoCentro.lng, novoCentro.lat]);
            }

            this.setState((prev) => ({
                drawnShapes: prev.drawnShapes.map((s) => s.drawId === feat.id
                    ? {
                        ...s,
                        feature: this.draw.get(feat.id) || feat,
                        center: novoCentro ? [novoCentro.lng, novoCentro.lat] : (feat.properties?.center || s.center),
                        radiusMeters: feat.properties?.radiusMeters || s.radiusMeters,
                    }
                    : s),
            }));
        });

        this.applyShapeFilter();

        // A forma mudou de lugar/tamanho: os cards do painel de resultados precisam ser
        // recalculados com os empreendimentos que ficaram dentro dela (a forma que ficou
        // vazia perde o card, dentro de refreshShapeAnalyses).
        const movedShapes = this.state.drawnShapes.filter(
            (s) => updatedFeatures.some((feat) => feat.id === s.drawId)
        );
        if (movedShapes.length > 0) {
            this.refreshShapeAnalyses(movedShapes, 'Nenhum empreendimento na nova posição da área.');
        }
    };

    // Ids removidos do draw intencionalmente por "esconder" — a forma deve
    // permanecer na lista (apenas oculta), então handleDrawDelete os ignora.
    _hidingIds = new Set();

    handleDrawDelete = (e) => {
        const deletedIds = (e.features || [])
            .map((f) => f.id)
            .filter((id) => {
                if (this._hidingIds.has(id)) { this._hidingIds.delete(id); return false; }
                return true;
            });
        if (deletedIds.length === 0) { this.applyShapeFilter(); return; }

        this.removerAreasDoPainel(
            this.state.drawnShapes.filter((s) => deletedIds.includes(s.drawId)).map((s) => s.areaId)
        );
        this.setState((prev) => ({
            drawnShapes: prev.drawnShapes.filter((s) => !deletedIds.includes(s.drawId)),
        }));
        this.applyShapeFilter();
    };

    // ===== KML: exportação e importação =====

    // Exporta as formas desenhadas (polígonos + círculos) e os empreendimentos
    // exibidos como KML, reusando o backend do mapa Google (KmlService.download →
    // route('download-kml-mapa')). Círculos do draw são turf polygons com
    // properties.center/radiusMeters; convertidos de volta para {lat,lng,raio}.
    handleDownloadKml = async () => {
        const features = this.draw?.getAll().features || [];
        const polygons = [];
        const circles = [];

        features.forEach((f) => {
            if (f.geometry?.type !== 'Polygon') return;
            const isCircle = ['circle', 'marker_circle'].includes(f.properties?.shapeType);
            if (isCircle && f.properties?.center && f.properties?.radiusMeters) {
                circles.push({
                    latitude: f.properties.center[1],
                    longitude: f.properties.center[0],
                    radius: f.properties.radiusMeters,
                });
            } else {
                // Anel externo GeoJSON [lng,lat] → [lat,lng] esperado pelo KmlCreator.
                const ring = f.geometry.coordinates?.[0] || [];
                polygons.push(ring.map(([lng, lat]) => [lat, lng]));
            }
        });

        // Empreendimentos dentro das formas (sem formas, todos os carregados),
        // mapeados para o formato esperado pelo backend. O conjunto de campos segue o
        // ExtendedData do KmlCreator (Status, Tipo, Padrão, Ano de Lançamento, Preço
        // Médio, Área Média, Endereço, Cidade, Bairro); valores zerados viram null para
        // o writer omitir a linha em vez de exportar "0,00".
        const sourceFeatures = this.insideFeatures || [];
        const empreendimentos = sourceFeatures.map((f) => {
            const p = f.properties || {};
            return {
                _id: p.id,
                nome: p.name || '',
                status: p.status || '',
                tipo_empreendimento: p.type || '',
                padrao: p.padrao || '',
                ano_lancamento: p.data_lancamento ? String(p.data_lancamento).slice(0, 4) : '',
                ticket_medio: Number(p.ticket_medio) > 0 ? Number(p.ticket_medio) : null,
                area_media: Number(p.area_media) > 0 ? Number(p.area_media) : null,
                endereco: p.address || '',
                cidade: p.city || '',
                bairro: p.neighborhood || '',
                marker: p.icon || '',
                location: { coordinates: f.geometry?.coordinates || null },
            };
        });

        if (polygons.length === 0 && circles.length === 0 && empreendimentos.length === 0) {
            toast.info('Nada para exportar. Desenhe uma forma ou faça uma busca primeiro.');
            return;
        }

        try {
            this.setState({ loadingKml: true });
            await KmlService.download({ polygons, circles, empreendimentos });
        } catch (e) {
            console.error('[MapboxMap] erro ao exportar KML:', e);
            toast.error('Erro ao exportar o KML.');
        } finally {
            this.setState({ loadingKml: false });
        }
    };

    // Importa KML/KMZ: envia ao backend (route('transform-kml')), que devolve os
    // polígonos como arrays de [lat,lng]; cada um é adicionado ao draw.
    handleImportKmlFile = async (file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);

        try {
            this.setState({ loadingKml: true });
            const response = await axios.post(route('transform-kml'), formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.status !== 200 || !Array.isArray(response.data) || response.data.length === 0) {
                toast.warn('Nenhum polígono encontrado no arquivo.');
                return;
            }

            await this.addImportedPolygons(response.data);
            toast.success(`${response.data.length} forma(s) importada(s).`);
        } catch (e) {
            console.error('[MapboxMap] erro ao importar KML/KMZ:', e);
            toast.error('Erro ao importar o arquivo KML/KMZ.');
        } finally {
            this.setState({ loadingKml: false });
        }
    };

    // Adiciona ao draw os polígonos importados (cada path = array de [lat,lng]),
    // registra na lista de formas, centraliza o mapa e dispara o carregamento dos
    // empreendimentos dentro das formas (via cidades resolvidas por geocodificação reversa).
    addImportedPolygons = async (paths) => {
        if (!this.draw) return;

        const newShapes = [];
        const centers = [];
        const allBounds = new mapboxgl.LngLatBounds();

        paths.forEach((path, i) => {
            if (!Array.isArray(path) || path.length < 3) return;

            const ring = path.map(([lat, lng]) => [lng, lat]);
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first);

            const feature = { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } };
            const [drawId] = this.draw.add(feature);

            const idx = this.state.drawnShapes.length + newShapes.length;
            const type = 'Polígono';
            const name = `${type} ${idx + 1}`;
            const color = colors[idx % colors.length];
            const areaId = `drawn-${Date.now()}-${i}`;
            const styleAttrs = { fillColor: color, fillOpacity: 0.5, strokeColor: '#000000', strokeWeight: 3 };
            Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(drawId, k, v));

            // Centróide (centro do bounding box) p/ geocodificação reversa → cidade.
            const b = new mapboxgl.LngLatBounds();
            ring.forEach((c) => { b.extend(c); allBounds.extend(c); });
            const ctr = b.getCenter();
            centers.push({ lat: ctr.lat, lng: ctr.lng });

            newShapes.push({
                drawId, areaId, type, name, color, visible: true,
                feature: this.draw.get(drawId), center: null, radiusMeters: null, ...styleAttrs,
            });
        });

        if (newShapes.length === 0) {
            toast.warn('Nenhuma forma válida no arquivo.');
            return;
        }

        await new Promise((resolve) =>
            this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, ...newShapes] }), resolve)
        );

        if (!allBounds.isEmpty()) {
            this.map.fitBounds(allBounds, { padding: 60, duration: 600 });
        }

        // Carrega do backend os empreendimentos dentro das formas importadas.
        await this.loadEmpreendimentosForImportedShapes(centers, newShapes);
    };

    // Resolve as cidades das formas importadas (geocodificação reversa dos centróides),
    // sincroniza o filtro de cidade, busca os empreendimentos dessas cidades e recorta
    // pelas formas (applyShapeFilter, dentro de fetchAndRender). Em seguida roda a análise
    // por forma para popular o painel de resultados.
    loadEmpreendimentosForImportedShapes = async (centers, importedShapes) => {
        const resolved = await this.resolverCidadesDeCentros(centers);

        // União com as cidades já selecionadas (preserva a busca atual).
        const current = this.formFiltersRef.current?.getFilters?.()?.cidades || [];
        const byValue = new Map();
        [...current, ...resolved].forEach((c) => byValue.set(c.value, c));
        const cidades = [...byValue.values()];

        if (cidades.length === 0) {
            toast.info('Não foi possível identificar a cidade das formas importadas. Selecione uma cidade e busque.');
            return;
        }

        // Sincroniza a cidade no formulário (prop observada por FormFiltersContainer).
        this.setState({ cidadeSincronizada: cidades });

        const currentFilters = this.formFiltersRef.current?.getFilters?.() || {};
        const payload = this.buildFiltersPayload({ ...currentFilters, cidades });

        // Carrega allFeatures e recorta pelas formas (incl. as importadas).
        await this.fetchAndRender(payload);

        // Análise por forma importada (popula o painel de resultados).
        this.runShapeAnalyses(importedShapes);

        this.refreshBairrosAvailability(cidades);
        this.refreshZoneamentoAvailability(cidades);
        this.loadYearsOptionsForLinkagesDataLayer(cidades);
    };

    /**
     * Opções de cidade ({value, label}) para uma lista de pontos, por geocodificação
     * reversa. Pontos sem cidade/UF identificável são ignorados — a lista pode voltar
     * menor do que a de entrada (ou vazia).
     */
    resolverCidadesDeCentros = async (centers) => {
        const resolved = [];
        for (const c of centers) {
            try {
                const r = await geolocationService.reverse(c.lat, c.lng, null);
                if (r?.uf && r?.cidade) {
                    resolved.push({ value: `${r.uf}-${r.cidade}`, label: `${r.cidade} - ${r.uf}` });
                }
            } catch (e) {
                console.error('[MapboxMap] falha na geocodificação reversa de forma:', e);
            }
        }
        return resolved;
    };

    /** Bounding box de uma geometria GeoJSON como LngLatBounds, ou null. */
    boundsDaGeometria = (geometry) => {
        const coordinates = geometry?.coordinates;
        if (!coordinates) return null;

        const bounds = new mapboxgl.LngLatBounds();
        const extend = (coords) => {
            if (typeof coords[0] === 'number') { bounds.extend(coords); return; }
            coords.forEach(extend);
        };
        extend(coordinates);

        return bounds.isEmpty() ? null : bounds;
    };

    /** Centro do bounding box de uma geometria GeoJSON ({lat, lng}), ou null. */
    centroDaGeometria = (geometry) => {
        const center = this.boundsDaGeometria(geometry)?.getCenter();
        return center ? { lat: center.lat, lng: center.lng } : null;
    };

    /**
     * Bounding box das formas visíveis no formato que o backend espera
     * ([oeste, sul, leste, norte]). É o que faz a busca cobrir todas as cidades sob a
     * forma — inclusive as que não estão no filtro de cidade; o recorte exato pelo
     * polígono continua sendo feito aqui (applyShapeFilter).
     */
    bboxesDasFormasVisiveis = () =>
        this.state.drawnShapes
            .filter((s) => s.visible)
            .map((s) => this.boundsDaGeometria((this.draw?.get(s.drawId) || s.feature)?.geometry))
            .filter(Boolean)
            .map((bounds) => {
                const [[oeste, sul], [leste, norte]] = bounds.toArray();
                return [oeste, sul, leste, norte];
            });

    /** Centro de uma forma desenhada, a partir da geometria atual dela no draw. */
    centroDaForma = (shape) =>
        this.centroDaGeometria((this.draw?.get(shape.drawId) || shape.feature)?.geometry);

    /**
     * Cidades das formas desenhadas, identificadas pelo centro de cada uma (mesmo
     * caminho da importação de KML). Duplicatas são descartadas — várias formas na
     * mesma cidade carregam a cidade uma vez só.
     */
    cidadesDasFormas = async (shapes) => {
        const centers = shapes.map(this.centroDaForma).filter(Boolean);
        const byValue = new Map();
        (await this.resolverCidadesDeCentros(centers)).forEach((c) => byValue.set(c.value, c));
        return [...byValue.values()];
    };

    // ===== Detalhe do empreendimento (modal ao clicar no marcador) =====

    // Busca o detalhe completo do empreendimento (fotos, unidades, tabelas, etc.)
    // pelo id do Building e abre o ModalEmpreendimentos (reuso do mapa Google).
    // As sidebars são não-modais (ficam sobre o mapa, acima da máscara do modal), então
    // competem visualmente com o detalhe do empreendimento: enquanto ele está aberto elas
    // são escondidas por CSS (.mapbox-sidebar-oculta) e não por `visible`, porque a Sidebar
    // do PrimeReact desmonta o conteúdo ao fechar (CSSTransition unmountOnExit) — o que
    // zerava os filtros, a lista de formas e a ref do formulário a cada abertura do modal.

    openEmpreendimentoModal = async (buildingId) => {
        if (!buildingId || this.state.loadingEmpreendimentoDetail) return;

        this.setState({ loadingEmpreendimentoDetail: true });
        try {
            const res = await axios.get(`/api/empreendimentos-mapbox/${buildingId}/detalhe`);
            this.setState({ empreendimentoDetail: res.data, showEmpreendimentoModal: true });
        } catch (e) {
            console.error('[MapboxMap] erro ao carregar detalhe do empreendimento:', e);
            toast.error('Não foi possível carregar os detalhes do empreendimento.');
        } finally {
            this.setState({ loadingEmpreendimentoDetail: false });
        }
    };

    closeEmpreendimentoModal = () => {
        this.restoreDragPan();
        this.setState({ showEmpreendimentoModal: false, empreendimentoDetail: null });
    };

    // ===== Ações da lista de formas desenhadas =====
    updateShape = (drawId, patch) => this.setState((prev) => ({
        drawnShapes: prev.drawnShapes.map((s) => s.drawId === drawId ? { ...s, ...patch } : s),
    }));

    centerOnShape = (shape) => {
        const feat = shape.visible ? this.draw?.get(shape.drawId) : shape.feature;
        if (!feat?.geometry?.coordinates) return;

        const bounds = new mapboxgl.LngLatBounds();
        const walk = (coords) => Array.isArray(coords[0]) ? coords.forEach(walk) : bounds.extend(coords);
        walk(feat.geometry.coordinates);
        this.map.fitBounds(bounds, { padding: 60, duration: 600 });
    };

    /** Remove cards do painel de resultados, fechando-o quando não sobra nenhum. */
    removerAreasDoPainel = (areaIds) => {
        if (areaIds.length === 0) return;

        this.setState((prev) => {
            const searchAreas = prev.searchAreas.filter((a) => !areaIds.includes(a.id));
            return { searchAreas, right_panel_visible: searchAreas.length > 0 && prev.right_panel_visible };
        });
    };

    // Forma oculta não recorta a busca (ver handleFiltersSubmit), então também não
    // figura no painel de resultados: o card sai ao ocultar e é refeito ao reexibir.
    toggleShapeVisibility = (shape) => {
        if (shape.visible) {
            const feat = this.draw?.get(shape.drawId) || shape.feature;
            this._hidingIds.add(shape.drawId); // não remover da lista no draw.delete resultante
            this.draw?.delete(shape.drawId);
            this.updateShape(shape.drawId, { visible: false, feature: feat });
            this.removerAreasDoPainel([shape.areaId]);
            this.applyShapeFilter();
            return;
        }

        const [id] = this.draw?.add(shape.feature) || [];
        const visibleShape = { ...shape, visible: true, drawId: id ?? shape.drawId };
        this.updateShape(shape.drawId, { visible: true, drawId: id ?? shape.drawId });
        this.applyShapeFilter();
        this.refreshShapeAnalyses([visibleShape], 'Nenhum empreendimento na área reexibida.');
    };

    setShapeRadius = (shape, radiusMeters) => {
        if (!shape.center || !(radiusMeters > 0)) return;
        const shapeType = shape.feature?.properties?.shapeType || 'circle';
        const updated = makeCircleFeature(shape.center, radiusMeters, shapeType);
        // Preserva os atributos de estilo (cor/opacidade/borda) ao regenerar a geometria.
        updated.properties = {
            ...updated.properties,
            fillColor: shape.fillColor,
            fillOpacity: shape.fillOpacity,
            strokeColor: shape.strokeColor,
            strokeWeight: shape.strokeWeight,
        };
        // Mantém o mesmo id → MapboxDraw atualiza a feature no lugar (sem delete+add,
        // logo sem disparar draw.delete nem race de renderização).
        updated.id = shape.drawId;

        if (shape.visible) this.draw?.add(updated);
        this.updateShape(shape.drawId, { radiusMeters, feature: updated });
        this.applyShapeFilter();

        // Raio novo, área nova: o card do painel precisa ser recalculado.
        if (shape.visible) {
            this.refreshShapeAnalyses(
                [{ ...shape, radiusMeters, feature: updated }],
                'Nenhum empreendimento no novo raio da área.'
            );
        }
    };

    removeShape = (shape) => {
        // draw.delete() programático não dispara o evento draw.delete, então a
        // limpeza da lista/área é feita aqui mesmo (a lixeira nativa usa handleDrawDelete).
        if (shape.visible) this.draw?.delete(shape.drawId);
        this._hidingIds.delete(shape.drawId);
        this.removerAreasDoPainel([shape.areaId]);
        this.setState((prev) => ({
            drawnShapes: prev.drawnShapes.filter((s) => s.drawId !== shape.drawId),
        }));
        this.applyShapeFilter();
    };

    editShapeAttributes = (shape) => this.setState({ shapeEditing: shape, shapeEditorOpen: true });

    // Ver restoreDragPan em drawModes.js: sem isso o mapa perde o clique-e-arrasto
    // de vez quando um diálogo engole o mouseup de um arraste sobre uma forma.
    restoreDragPan = () => restoreDragPan(this.map);

    closeShapeEditor = () => {
        this.restoreDragPan();
        this.setState({ shapeEditorOpen: false, shapeEditing: null });
    };

    // Aplica os atributos editados (cor/opacidade/borda/raio/rótulo) à forma.
    // Reusa o padrão "re-add com mesmo id" (igual setShapeRadius): MapboxDraw
    // atualiza a feature no lugar, sem delete+add.
    applyShapeAttributes = (shape, attrs) => {
        if (!shape) return;

        const base = (shape.visible && this.draw?.get(shape.drawId)) || shape.feature;
        const updated = {
            ...base,
            id: shape.drawId,
            properties: {
                ...base.properties,
                fillColor: attrs.fillColor,
                fillOpacity: attrs.fillOpacity,
                strokeColor: attrs.strokeColor,
                strokeWeight: attrs.strokeWeight,
            },
        };

        // Raio (somente círculo): regenera a geometria mantendo o centro.
        const raioAlterado = shape.type === 'Círculo'
            && attrs.radiusMeters > 0
            && attrs.radiusMeters !== shape.radiusMeters;

        if (shape.type === 'Círculo' && attrs.radiusMeters > 0 && shape.center) {
            const st = updated.properties?.shapeType || 'circle';
            const geo = makeCircleFeature(shape.center, attrs.radiusMeters, st);
            updated.geometry = geo.geometry;
            updated.properties = { ...updated.properties, radiusMeters: attrs.radiusMeters };
        }

        if (shape.visible) {
            this.draw?.add(updated);
            // Antes do changeMode: o novo onSetup do simple_select congela o estado
            // atual do dragPan (ver restoreDragPan em drawModes.js).
            this.restoreDragPan();
            // Sai do estado "ativo" (laranja de edição) para que a cor/opacidade
            // customizada da forma apareça imediatamente (drawStyles só aplica
            // user_* quando active=false).
            this.draw?.changeMode('simple_select', { featureIds: [] });
        }

        const name = (attrs.name || '').trim() || shape.name;
        const cor = attrs.fillColor === 'transparent' ? attrs.strokeColor : attrs.fillColor;

        this.updateShape(shape.drawId, {
            name,
            color: cor,
            fillColor: attrs.fillColor,
            fillOpacity: attrs.fillOpacity,
            strokeColor: attrs.strokeColor,
            strokeWeight: attrs.strokeWeight,
            radiusMeters: shape.type === 'Círculo' && attrs.radiusMeters > 0 ? attrs.radiusMeters : shape.radiusMeters,
            feature: updated,
        });

        // Sincroniza nome/cor no painel de resultados.
        this.setState((prev) => ({
            searchAreas: prev.searchAreas.map((a) => a.id === shape.areaId ? { ...a, name, color: cor } : a),
        }));

        this.applyShapeFilter();

        // Cor e rótulo só mudam a aparência do card; o raio muda a área analisada.
        if (raioAlterado && shape.visible) {
            this.refreshShapeAnalyses(
                [{ ...shape, name, color: cor, radiusMeters: attrs.radiusMeters, feature: updated }],
                'Nenhum empreendimento no novo raio da área.'
            );
        }

        this.closeShapeEditor();
    };

    /*
     * ===== Régua e Raio por Deslocamento (isócrona) =====
     *
     * Implementação em ./features/isochroneFeature.js, compartilhada com os demais
     * mapas Mapbox. As duas ferramentas vivem juntas por serem mutuamente exclusivas.
     */
    toggleRuler = () => isochrone.toggleRuler(this);
    deactivateRuler = () => isochrone.deactivateRuler(this);
    toggleIsochronePick = () => isochrone.toggleIsochronePick(this);
    handleIsochroneCenterPick = (e) => isochrone.handleIsochroneCenterPick(this, e);
    handleIsochronesReady = (isochrones, tipoLabel) => isochrone.handleIsochronesReady(this, isochrones, tipoLabel);

    // Análise por área (como no handleDrawCreate) para formas adicionadas
    // programaticamente (isócronas, projeto carregado), se há empreendimentos carregados.
    runShapeAnalyses = (shapes) => {
        if (this.allFeatures.length === 0) return;
        shapes.forEach((shape) => {
            const buildingIds = this.buildingsInsideShape(shape).map((f) => f.properties.id);

            if (buildingIds.length > 0) {
                this.fetchAnalysisByBuildings(buildingIds, shape.name, shape.areaId, shape.color);
            }
        });
    };

    // Empreendimentos atualmente carregados que caem dentro de uma forma.
    buildingsInsideShape = (shape) => {
        const feature = this.draw?.get(shape.drawId) || shape.feature;
        if (!feature) return [];

        return this.allFeatures.filter((point) => {
            try {
                return booleanPointInPolygon(point, feature);
            } catch {
                return false;
            }
        });
    };

    // Reexecuta a análise das formas desenhadas após uma nova busca ou depois de
    // mover/redimensionar uma forma: cada card é atualizado no lugar com os
    // empreendimentos que ficaram dentro dela, e as formas que ficaram sem nenhum
    // perdem o card.
    refreshShapeAnalyses = async (
        shapes,
        mensagemSemResultado = 'Nenhum empreendimento encontrado nas áreas desenhadas para os filtros selecionados.'
    ) => {
        const analyses = shapes
            .map((shape) => ({ shape, buildings: this.buildingsInsideShape(shape) }))
            .filter(({ buildings }) => buildings.length > 0);

        const staleAreaIds = shapes
            .filter((s) => !analyses.some((a) => a.shape.areaId === s.areaId))
            .map((s) => s.areaId);

        this.removerAreasDoPainel(staleAreaIds);

        if (analyses.length === 0) {
            // Busca sem nenhum resultado já é avisada por fetchAndRender.
            if (this.allFeatures.length > 0) {
                toast.info(mensagemSemResultado);
            }
            return;
        }

        await Promise.all(analyses.map(({ shape, buildings }) =>
            this.fetchAnalysisByBuildings(buildings.map((f) => f.properties.id), shape.name, shape.areaId, shape.color)
        ));
    };

    fetchAnalysisByBuildings = async (buildingIds, areaName, areaId, color) => {
        const card = { id: areaId, name: areaName, color, loading: true, meta_data: null, isDrawnArea: true, filtros: this.currentFilterBadges() };

        this.setState((prev) => ({
            // Reanálise da mesma forma atualiza o card existente; análise nova entra
            // no TOPO do painel de resultados.
            searchAreas: prev.searchAreas.some((a) => a.id === areaId)
                ? prev.searchAreas.map((a) => (a.id === areaId ? card : a))
                : [card, ...prev.searchAreas],
            right_panel_visible: true,
        }));

        try {
            const res = await axios.post('/api/empreendimentos-mapbox/analysis-by-area', { building_ids: buildingIds });
            const { meta_data } = res.data;

            this.setState((prev) => ({
                searchAreas: prev.searchAreas.map((a) =>
                    a.id === areaId ? { ...a, meta_data, loading: false } : a
                ),
            }));
        } catch (e) {
            console.error('[MapboxMap] erro análise área desenhada:', e);
            // 422: a área passou do teto de empreendimentos por análise — a mensagem do
            // backend explica o que fazer, e é mais útil que o erro genérico.
            const motivo = e.response?.status === 422
                ? e.response?.data?.errors?.building_ids?.[0]
                : null;
            toast.error(motivo || 'Erro ao carregar dados de análise da área.');
            this.setState((prev) => ({
                searchAreas: prev.searchAreas.filter((a) => a.id !== areaId),
            }));
        }
    };

    // ===== Projetos (salvar / carregar / excluir / compartilhar) =====
    // Reusa o backend do mapa Google (MapaState/StoreMapaProjectService), serializando
    // as formas no formato legado para que projetos sejam intercambiáveis entre os mapas.

    // Formas do draw → formato legado: polígonos como caminhos [[lat,lng],...] (sem o
    // ponto de fechamento) e círculos como {latitude, longitude, radius}.
    serializeShapesForProject = () => {
        const polygons = [];
        const circles = [];

        this.state.drawnShapes.forEach((shape) => {
            const feature = this.draw?.get(shape.drawId) || shape.feature;
            if (!feature) return;

            const style = {
                color: shape.fillColor,
                fillColor: shape.fillColor,
                strokeColor: shape.strokeColor,
                fillOpacity: shape.fillOpacity,
                strokeWeight: shape.strokeWeight,
            };

            if (['circle', 'marker_circle'].includes(feature.properties?.shapeType)) {
                const center = feature.properties.center || shape.center;
                const radius = feature.properties.radiusMeters || shape.radiusMeters;
                if (!center || !radius) return;
                circles.push({
                    latitude: center[1],
                    longitude: center[0],
                    radius,
                    name: shape.name,
                    type: shape.type,
                    ...style,
                });
                return;
            }

            if (feature.geometry?.type !== 'Polygon') return;
            const ring = feature.geometry.coordinates[0] || [];
            const path = ring.slice(0, -1).map(([lng, lat]) => [lat, lng]);
            if (path.length < 3) return;
            polygons.push({ name: shape.name, polygon: path, ...style });
        });

        return { polygons, circles };
    };

    // Payload no shape que o StoreMapaProjectService espera (o service acessa a maioria
    // das chaves incondicionalmente — todas precisam existir, mesmo vazias).
    createProjectPayload = () => {
        const filters = this.formFiltersRef.current?.getFilters?.() || {};
        const { polygons, circles } = this.serializeShapesForProject();
        const center = this.map?.getCenter();

        return {
            project_name: this.state.projectName,
            share_users: this.state.projectShareUsers.map((u) => u.id),
            empreendimentos: [],
            address: null,
            cidade: filters.cidades || [],
            estado: filters.estados || [],
            tipo_empreendimento: filters.tipo || [],
            padrao: filters.padrao || [],
            status: filters.status || [],
            incorporadoras: filters.incorporadoras || [],
            // Opções {value: id, label: nome}, como saem do autocomplete: é o formato que
            // buildFiltersPayload espera de volta ao carregar o projeto.
            nome_empreendimento: filters.nome_empreendimento || [],
            bairros: filters.bairros || [],
            quartos: filters.quartos || [],
            ano_lancamento: filters.ano_lancamento || [],
            estagio: filters.estagio || [],
            retrofit: filters.retrofit || [],
            polygons,
            circles,
            area_maior_que: filters.area_maior_que || null,
            area_menor_que: filters.area_menor_que || null,
            valor_maior_que: filters.valor_maior_que || null,
            valor_menor_que: filters.valor_menor_que || null,
            valor_metro_maior_que: filters.valor_metro_maior_que || null,
            valor_metro_menor_que: filters.valor_metro_menor_que || null,
            juros_minimo: filters.juros_minimo || null,
            juros_maximo: filters.juros_maximo || null,
            valor_parcela_minima: filters.valor_parcela_minima || null,
            valor_parcela_maxima: filters.valor_parcela_maxima || null,
            parcelas_minimo: filters.parcelas_minimo || null,
            parcelas_maximo: filters.parcelas_maximo || null,
            porcentagem_vendida_minima: filters.porcentagem_vendida_minima || null,
            porcentagem_vendida_maxima: filters.porcentagem_vendida_maxima || null,
            mes_ano_lancamento: filters.mes_ano_lancamento || null,
            currentPosition: { lat: center?.lat ?? null, lng: center?.lng ?? null },
            raio: 3000,
            ranges: this.state.ranges,
            color_ranges: Object.keys(this.state.color_ranges || {}).length ? this.state.color_ranges : null,
            datalayerName: this.state.datalayerName || null,
            datalayerColorProperty: this.state.datalayerColorProperty || null,
            datalayerYear: this.state.datalayerYear || null,
            datalayerYears: this.state.datalayerYears?.length ? this.state.datalayerYears : null,
            datalayer: null,
        };
    };

    saveProject = async () => {
        this.setState({ savingProject: true, projectErrors: {} });
        try {
            const response = await axios.post('/mapa-state/projeto', this.createProjectPayload());
            toast.success(response.data.message);
            this.setState({ modalSalvarProjetoOpen: false, projectName: '', projectShareUsers: [] });
            this.loadProjetos();
        } catch (error) {
            console.error('[MapboxMap] erro ao salvar projeto:', error);
            if (error.response?.data?.errors) {
                this.setState({ projectErrors: error.response.data.errors });
            } else {
                toast.error('Erro ao salvar o projeto.');
            }
        } finally {
            this.setState({ savingProject: false });
        }
    };

    // Campos do projeto chegam como string JSON ou já decodificados (o show() do
    // MapaState converte alguns para arrays de valores) — normaliza os dois casos.
    parseProjectField = (value, fallback = []) => {
        if (value == null) return fallback;
        if (typeof value === 'string') {
            try {
                return JSON.parse(value) ?? fallback;
            } catch {
                return fallback;
            }
        }
        return value;
    };

    // Status do projeto já traduzido da nomenclatura legada (ver STATUS_LEGADO).
    parseProjectStatus = (value) => {
        const status = this.parseProjectField(value, ['Ativo']);
        return [...new Set(status.map((s) => STATUS_LEGADO[s] ?? s))];
    };

    // Cidades/UFs do projeto sem repetição (o mapa Google salvava uma UF por cidade,
    // gerando duplicatas que virariam buscas e rótulos de análise repetidos).
    parseProjectCidades = (value) => {
        const cidades = this.parseProjectField(value);
        return [...new Map(cidades.map((c) => [c.value, c])).values()];
    };

    handleProjetoSelected = async (projectId) => {
        try {
            const response = await axios.get(route('mapa.project.show', projectId));
            const p = response.data.project;

            // Limpa o mapa (formas, camadas, áreas). Os filtros do form são
            // sobrescritos integralmente por applyProjectFilters logo abaixo.
            this.handleFiltersClear();

            await this.formFiltersRef.current?.applyProjectFilters?.({
                cidade: this.parseProjectCidades(p.cidade),
                estadosSelecionados: [...new Set(this.parseProjectField(p.estado))],
                status: this.parseProjectStatus(p.status),
                padrao: this.parseProjectField(p.padrao),
                tipo_empreendimento: this.parseProjectField(p.tipo_empreendimento),
                bairros: this.parseProjectField(p.bairros),
                // 'Studio' é um dos valores dos switches; só os demais são numéricos.
                quartos: this.parseProjectField(p.quartos).map((q) => (q === 'Studio' ? q : Number(q))),
                ano_lancamento: this.parseProjectField(p.ano_lancamento).map(Number),
                estagio: this.parseProjectField(p.estagio),
                // 0/1 — o show() do projeto devolve os valores já extraídos do formato legado.
                retrofit: this.parseProjectField(p.retrofit).map(Number),
                incorporadoras: this.parseProjectField(p.incorporadoras),
                // Projetos do mapa Google guardavam só o nome do empreendimento; sem o id
                // não filtram nada (ver buildFiltersPayload), então ficam fora do campo.
                nome_empreendimento: this.parseProjectField(p.nome_empreendimento)
                    .filter((item) => Number.isInteger(Number(item?.value))),
                area_maior_que: p.area_maior_que ?? '',
                area_menor_que: p.area_menor_que ?? '',
                valor_maior_que: p.valor_maior_que ?? '',
                valor_menor_que: p.valor_menor_que ?? '',
                valor_metro_maior_que: p.valor_metro_maior_que ?? '',
                valor_metro_menor_que: p.valor_metro_menor_que ?? '',
                juros_minimo: p.juros_minimo ?? '',
                juros_maximo: p.juros_maximo ?? '',
                parcelas_minimo: p.parcelas_minimo ?? '',
                parcelas_maximo: p.parcelas_maximo ?? '',
                valor_parcela_minima: p.valor_parcela_minima ?? '',
                valor_parcela_maxima: p.valor_parcela_maxima ?? '',
                porcentagem_vendida_minima: p.porcentagem_vendida_minima ?? '',
                porcentagem_vendida_maxima: p.porcentagem_vendida_maxima ?? '',
                mes_ano_lancamento: p.mes_ano_lancamento ? new Date(p.mes_ano_lancamento) : '',
            });

            // Ranges de marcadores + faixas de cor dos dados censitários
            // (JSON.stringify serializa Infinity como null; restaura ao carregar).
            const ranges = this.parseProjectField(p.ranges);
            const colorRanges = this.parseProjectField(p.color_ranges, null);
            if (colorRanges) {
                Object.keys(colorRanges).forEach((key) => {
                    if (Array.isArray(colorRanges[key])) {
                        colorRanges[key] = colorRanges[key].map((r) => ({
                            ...r,
                            min: r.min === null ? -Infinity : r.min,
                            max: r.max === null ? Infinity : r.max,
                        }));
                    }
                });
            }
            await new Promise((resolve) =>
                this.setState(
                    { ranges, color_ranges: colorRanges || {}, loadedProjectId: projectId },
                    resolve
                )
            );

            // Recria as formas salvas e dispara a busca com os filtros do projeto.
            const newShapes = this.addProjectShapes(
                this.parseProjectField(p.polignos),
                this.parseProjectField(p.manualCircles)
            );

            // handleFiltersSubmit já roda a análise de cada forma visível (refreshShapeAnalyses).
            const filters = this.formFiltersRef.current?.getFilters?.() || {};
            if ((filters.cidades || []).length > 0 || (filters.estados || []).length > 0) {
                await this.handleFiltersSubmit(filters);
            } else {
                await this.buscarPeloCentroDoProjeto(p, filters);
            }

            // Restaura a camada de dados censitários, se o projeto tinha uma ativa.
            const datalayerName = this.parseProjectField(p.datalayerName, '');
            if (datalayerName) {
                await new Promise((resolve) =>
                    this.setState(
                        {
                            datalayerName,
                            datalayerFriendlyName: LinkagesDataLayerService.getLayerLabel(datalayerName),
                            datalayerColorPropertyOptions: LinkagesDataLayerService.getOptionsForColorMap(datalayerName),
                            datalayerColorProperty: this.parseProjectField(p.datalayerColorProperty, null),
                            datalayerYear: this.parseProjectField(p.datalayerYear, null),
                            datalayerYears: this.parseProjectField(p.datalayerYears),
                        },
                        resolve
                    )
                );
                this.loadDataLayer();
            }

            toast.success('Projeto carregado.');
        } catch (e) {
            console.error('[MapboxMap] erro ao carregar projeto:', e);
            toast.error('Erro ao carregar o projeto.');
        }
    };

    /**
     * Projetos salvos sem cidade/estado (só com formas desenhadas, por exemplo) não têm
     * como alimentar a busca — mas guardam o centro do mapa. Identifica a cidade por ele
     * para que os empreendimentos voltem junto com as formas.
     */
    buscarPeloCentroDoProjeto = async (projeto, filters) => {
        const lat = parseFloat(projeto.lat);
        const lng = parseFloat(projeto.lng);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            toast.warn('O projeto não tem cidade nem estado salvos. Selecione uma cidade e clique em BUSCAR.');
            return;
        }

        const cidadeOption = await this.cidadeDasCoordenadas(lat, lng);
        if (!cidadeOption) return;

        this.setState({ cidadeSincronizada: [cidadeOption] });
        await this.handleFiltersSubmit({ ...filters, cidades: [cidadeOption] });
    };

    // Recria no draw as formas salvas no projeto (formato legado do mapa Google).
    addProjectShapes = (polygons, circles) => {
        if (!this.draw) return [];

        const newShapes = [];
        const allBounds = new mapboxgl.LngLatBounds();

        const register = (drawId, name, type, style) => {
            const idx = this.state.drawnShapes.length + newShapes.length;
            const color = (style.color || style.fillColor) || colors[idx % colors.length];
            const areaId = `drawn-${Date.now()}-${idx}`;
            const styleAttrs = {
                fillColor: color,
                fillOpacity: style.fillOpacity ?? 0.5,
                strokeColor: style.strokeColor || '#000000',
                strokeWeight: style.strokeWeight ?? 3,
            };
            Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(drawId, k, v));

            const feature = this.draw.get(drawId);
            newShapes.push({
                drawId, areaId, type, name: name || `${type} ${idx + 1}`, color, visible: true,
                feature,
                center: feature?.properties?.center || null,
                radiusMeters: feature?.properties?.radiusMeters || null,
                ...styleAttrs,
            });
        };

        (polygons || []).forEach((data) => {
            // Formato atual: {name, polygon: [[lat,lng],...], ...estilos}; projetos
            // antigos podem ter só o caminho.
            const path = data?.polygon ?? data;
            if (!Array.isArray(path) || path.length < 3) return;

            const ring = path.map(([lat, lng]) => [lng, lat]);
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);

            const [drawId] = this.draw.add({
                type: 'Feature',
                properties: {},
                geometry: { type: 'Polygon', coordinates: [ring] },
            });
            register(drawId, data?.name, 'Polígono', data || {});
            ring.forEach((c) => allBounds.extend(c));
        });

        (circles || []).forEach((data) => {
            if (!data) return;
            const lat = parseFloat(data.center?.lat ?? data.latitude);
            const lng = parseFloat(data.center?.lng ?? data.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lng) || !data.radius) return;

            const feature = makeCircleFeature([lng, lat], data.radius, 'circle');
            const [drawId] = this.draw.add(feature);
            register(drawId, data.name, data.type || 'Círculo', data);
            (feature.geometry.coordinates[0] || []).forEach((c) => allBounds.extend(c));
        });

        if (newShapes.length > 0) {
            this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, ...newShapes] }));
            if (!allBounds.isEmpty()) {
                this.map.fitBounds(allBounds, { padding: 60, duration: 600 });
            }
        }

        return newShapes;
    };

    deleteProject = async () => {
        const projectId = this.state.loadedProjectId;
        if (!projectId) return;
        if (!window.confirm('Excluir o projeto carregado? Esta ação não pode ser desfeita.')) return;

        try {
            const response = await axios.delete(route('mapa.project.delete', projectId));
            toast.success(response.data.message);
            this.formFiltersRef.current?.cleanState?.();
            this.loadProjetos();
        } catch (e) {
            console.error('[MapboxMap] erro ao excluir projeto:', e);
            toast.error('Erro ao excluir o projeto.');
        }
    };

    // ===== Ranges customizados de marcadores (ModalRanges) =====

    addRange = (range) => this.setState((prev) => ({ ranges: [...prev.ranges, range] }));

    updateRange = (range) =>
        this.setState((prev) => ({
            ranges: prev.ranges.map((r) => (r.id === range.id ? { ...r, ...range } : r)),
        }));

    removeRange = (range) =>
        this.setState((prev) => ({ ranges: prev.ranges.filter((r) => r.id !== range.id) }));

    // "Aplicar" do ModalRanges: registra as imagens dos marcadores no mapa, marca cada
    // feature com o range correspondente e re-renderiza a source.
    applyMarkerRanges = async () => {
        if (!this.map) return;
        await this.ensureRangeImages();
        this.tagFeaturesWithRanges();
        this.applyShapeFilter();
    };

    // Desenha um pin estilo Google PinElement (cabeça + haste) com as cores/escala do
    // range. Base 40x53px em escala 1, reduzida na camada por MARKER_ICON_SIZE.
    createRangePinImage = (marker) => {
        const scale = marker.scale || 1;
        const w = Math.max(8, Math.round(40 * scale));
        const h = Math.max(10, Math.round(53 * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');

        const cx = w / 2;
        const r = w * 0.42;
        const cy = r + 2 * scale;

        // Gota: arco da cabeça (de baixo-esquerda a baixo-direita, passando pelo topo)
        // + ponta inferior (âncora bottom da camada).
        ctx.beginPath();
        ctx.arc(cx, cy, r, Math.PI * 0.75, Math.PI * 0.25);
        ctx.lineTo(cx, h - 1);
        ctx.closePath();
        ctx.fillStyle = marker.background || '#cccccc';
        ctx.fill();
        ctx.lineWidth = Math.max(1, 2 * scale);
        ctx.strokeStyle = marker.borderColor || '#000000';
        ctx.stroke();

        // Glifo central.
        ctx.beginPath();
        ctx.arc(cx, cy, r * 0.35, 0, 2 * Math.PI);
        ctx.fillStyle = marker.glyphColor || '#000000';
        ctx.fill();

        return ctx.getImageData(0, 0, w, h);
    };

    rangeImageId = (range) => `range-marker-${range.id}`;

    // Garante que cada range tem sua imagem registrada no estilo atual (as imagens
    // são perdidas na troca de estilo). Cor → canvas; imagem → map.loadImage(url).
    ensureRangeImages = () =>
        Promise.all(
            this.state.ranges.map((range) => {
                const id = this.rangeImageId(range);
                if (this.map.hasImage(id)) return Promise.resolve();

                if (range.marker.type === 'image') {
                    return new Promise((resolve) => {
                        this.map.loadImage(range.marker.value, (err, image) => {
                            if (err || !image) {
                                toast.warn('Não foi possível carregar a imagem de um dos ranges.');
                                return resolve();
                            }
                            if (!this.map.hasImage(id)) this.map.addImage(id, image);
                            resolve();
                        });
                    });
                }

                this.map.addImage(id, this.createRangePinImage(range.marker));
                return Promise.resolve();
            })
        );

    // Marca cada feature com a imagem do primeiro range em que ela se encaixa
    // (mesma resolução do replaceMarkers do mapa Google). Sem match (ou sem ranges),
    // a camada cai no ícone padrão via coalesce.
    tagFeaturesWithRanges = () => {
        const { ranges } = this.state;
        this.allFeatures.forEach((f) => {
            delete f.properties.range_icon;
            if (ranges.length === 0) return;

            const range = ranges.find((r) => {
                if (r.type === 'padroes') return r.padroes.includes(f.properties.padrao);
                const value = Number(f.properties[RANGE_PROPERTY[r.type]] ?? NaN);
                return value >= r.min && value <= r.max;
            });

            if (range && this.map.hasImage(this.rangeImageId(range))) {
                f.properties.range_icon = this.rangeImageId(range);
            }
        });
    };

    /**
     * Esconde os rótulos de pontos de interesse do próprio MapBox (lojas, restaurantes,
     * escolas…), que competem visualmente com os marcadores de empreendimentos e com os
     * polos geradores. Todos os estilos usados aqui (ver MAP_STYLES) são clássicos, então
     * não têm `setConfigProperty`: a via é apagar as camadas da source-layer `poi_label`,
     * cujos ids variam entre estilos. Roda a cada troca de estilo, que recria as camadas.
     */
    hidePoiLabels = () => {
        (this.map.getStyle()?.layers || []).forEach((layer) => {
            if (layer['source-layer'] === 'poi_label') {
                this.map.setLayoutProperty(layer.id, 'visibility', 'none');
            }
        });
    };

    initializeLayers = async () => {
        this.hidePoiLabels();
        await this.loadMarkerImages();
        await this.loadHeatMarkerImages();
        await this.loadPolosImages();
        // Imagens dos ranges são perdidas na troca de estilo; re-registra antes do setData.
        if (this.state.ranges.length > 0) {
            await this.ensureRangeImages();
        }
        this.setupBairrosLayers();
        this.setupZoneamentoLayers();
        this.setupDatalayerLayers();
        this.setupClusterLayers();
        this.setupHeatLayers();
        this.setupPolosLayers();
        if (this.currentGeojson) {
            this.map.getSource(SOURCE_ID)?.setData(this.currentGeojson);
        }
        // Reaplica os limites de bairros se o toggle estiver ligado (ex.: após troca de estilo).
        if (this.state.showBairros) {
            const cidades = this.formFiltersRef.current?.getFilters?.()?.cidades || [];
            this.renderBairros(cidades);
        }
        // Reaplica o mapa de calor se estiver ativo (ex.: após troca de estilo, que
        // recria fontes/camadas), usando a última config escolhida.
        if (this.state.showHeatMap && this.heatMapConfig) {
            this.addHeatMap(this.heatMapConfig);
        }
        // Reaplica os polos geradores se estiverem ativos.
        if (this.state.showPolosGeradores && this.allPolos.length) {
            this.applyPolosShapeFilter();
            this.setPolosLayersVisibility(true);
        }
        // Reaplica a preferência de esconder empreendimentos (camadas recriadas visíveis).
        if (!this.state.showEmpreendimentos && !this.state.showHeatMap) {
            this.setEmpreendimentosVisibility(false);
        }
        // Reaplica zoneamento e dados censitários se estiverem ativos (ex.: após troca
        // de estilo, que descarta fontes e camadas).
        zoneamento.reapplyZoneamento(this);
        datalayer.reapplyDatalayer(this);

        // A troca de estilo descarta fonte e camadas da régua; recria se estiver medindo.
        this.ruler?.reapplyAfterStyleChange();

        this.reordenarCamadas();
        // Na troca de estilo o MapboxDraw recria as próprias camadas por conta própria,
        // e nem sempre antes daqui — o `idle` garante um reempilhamento com elas presentes.
        this.map.once('idle', this.reordenarCamadas);
    };

    /**
     * Reempilha as camadas próprias. O Mapbox desenha na ordem de inserção, então sem
     * isto as camadas criadas depois (sociodemografia, zoneamento, bairros, calor)
     * cobrem os desenhos — que são adicionados junto com o controle de desenho, antes
     * de tudo.
     *
     * Ordem final: dados → desenhos (e isócronas, que são formas do draw) → marcadores.
     * Os marcadores voltam para o topo porque o preenchimento das formas é translúcido
     * e os apagaria.
     */
    reordenarCamadas = () => {
        if (!this.map?.getStyle()) return;

        const camadasDoDraw = this.map.getStyle().layers
            .map((layer) => layer.id)
            .filter((id) => id.startsWith('gl-draw'));

        // A régua vai por último: a medição precisa ficar visível sobre tudo.
        [...camadasDoDraw, ...CLUSTER_LAYERS, 'heat-points', ...POLOS_LAYERS, ...RULER_LAYERS].forEach((id) => {
            if (this.map.getLayer(id)) this.map.moveLayer(id);
        });
    };

    setupBairrosLayers = () => {
        if (this.map.getSource(BAIRROS_SOURCE_ID)) return;

        this.map.addSource(BAIRROS_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });

        this.map.addLayer({
            id: 'bairros-fill',
            type: 'fill',
            source: BAIRROS_SOURCE_ID,
            paint: { 'fill-color': '#0d6efd', 'fill-opacity': 0.08 },
        });

        this.map.addLayer({
            id: 'bairros-outline',
            type: 'line',
            source: BAIRROS_SOURCE_ID,
            paint: { 'line-color': '#0d6efd', 'line-width': 1.5 },
        });

        this.map.addLayer({
            id: 'bairros-label',
            type: 'symbol',
            source: BAIRROS_SOURCE_ID,
            layout: {
                'text-field': ['get', 'name'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 11,
            },
            paint: {
                'text-color': '#0a4dad',
                'text-halo-color': '#ffffff',
                'text-halo-width': 1.2,
            },
        });
    };

    /*
     * ===== Zoneamento e Dados censitários (camadas Linkages/IBGE) =====
     *
     * A implementação vive em ./features/, compartilhada com os demais mapas Mapbox.
     * Os métodos abaixo são só a ponte: as features recebem a instância do componente
     * e usam o contrato getCidadesSelecionadas()/getFormasDesenhadas().
     */

    /** @returns {Array<{value: string, label: string}>} cidades selecionadas no formulário. */
    getCidadesSelecionadas = () => this.formFiltersRef.current?.getFilters?.()?.cidades || [];

    /** @returns {Array} features do MapboxDraw (formas desenhadas). */
    getFormasDesenhadas = () => this.draw?.getAll().features || [];

    setupDatalayerLayers = () => datalayer.setupDatalayerLayers(this);
    setupZoneamentoLayers = () => zoneamento.setupZoneamentoLayers(this);

    showZoneamentoLayer = () => zoneamento.showZoneamentoLayer(this);
    handleFiltrarZoneamento = (filtro) => zoneamento.filtrarZoneamento(this, filtro);
    removeZoneamentoLayer = () => zoneamento.removeZoneamentoLayer(this);
    toggleShowZoneamento = (enabled) => zoneamento.toggleShowZoneamento(this, enabled);
    refreshZoneamentoAvailability = (cidades) => zoneamento.refreshZoneamentoAvailability(this, cidades);

    loadSociodemografiaPermissions = () => datalayer.loadSociodemografiaPermissions(this);
    camadaRequerPropriedadeParaColorirMapa = () => datalayer.camadaRequerPropriedadeParaColorirMapa(this);
    camadaRequerAno = () => datalayer.camadaRequerAno(this);
    camadaRequerMultiplosAnos = () => datalayer.camadaRequerMultiplosAnos(this);
    onChangeDatalayerName = (event) => datalayer.onChangeDatalayerName(this, event);
    onChangeDatalayerColorProperty = (event) => datalayer.onChangeDatalayerColorProperty(this, event);
    onChangeDatalayerYear = (event) => datalayer.onChangeDatalayerYear(this, event);
    onChangeDatalayerYears = (event) => datalayer.onChangeDatalayerYears(this, event);
    onChangeOpacidadePreenchimento = (e, newValue) => datalayer.onChangeOpacidadePreenchimento(this, e, newValue);
    setDemarcacaoAreas = (value) => datalayer.setDemarcacaoAreas(this, value);
    getColorRangeKey = () => datalayer.getColorRangeKey(this);
    buildSensoPayload = (field) => datalayer.buildSensoPayload(this, field);
    loadDataLayer = () => datalayer.loadDataLayer(this);
    removeDataLayer = () => datalayer.removeDataLayer(this);
    handleAplyColorRanges = () => datalayer.handleAplyColorRanges(this);
    updateRendaColorRange = (colorRange, index) => datalayer.updateRendaColorRange(this, colorRange, index);
    replaceColorRanges = (colorRanges) => datalayer.replaceColorRanges(this, colorRanges);
    removeRendaColorRange = (index) => datalayer.removeRendaColorRange(this, index);
    loadDataLayerAggregated = () => datalayer.loadDataLayerAggregated(this);
    loadYearsOptionsForLinkagesDataLayer = (cidades) => datalayer.loadYearsOptionsForLinkagesDataLayer(this, cidades);
    overlaysForDatalayerTable = () => datalayer.overlaysForDatalayerTable(this);

    openModalLegendaSetores = () => this.setState({ show_modal_customizar_cores_setores: true });
    closeModalLegendaSetores = () => this.setState({ show_modal_customizar_cores_setores: false });
    closeModalDatalayerTable = () => this.setState({ show_modal_datalayer_datatable: false });

    setMapStyle = (style) => {
        if (!MAP_STYLES[style] || style === this.state.mapStyle) return;
        this.setState({ mapStyle: style });
        try {
            localStorage.setItem(MAP_STYLE_STORAGE_KEY, style);
        } catch (e) {
            console.warn('[MapboxMap] localStorage indisponível:', e);
        }
        const drawnShapes = this.draw?.getAll();
        this.map.setStyle(MAP_STYLES[style]);
        this.map.once('style.load', () => {
            this.initializeLayers();
            if (drawnShapes?.features?.length) {
                this.draw.set(drawnShapes);
                this.applyShapeFilter();
            }
        });
    };

    componentWillUnmount() {
        // Antes do map.remove(): a régua escuta o teclado no document.
        this.ruler?.destroy();
        if (this.map) this.map.remove();
    }

    /*
     * ===== Captura de tela =====
     * Implementação em ./features/mapCapture.js, compartilhada com os demais mapas Mapbox.
     */
    printMap = () => mapCapture.printMap(this);
    closeMapCapture = () => mapCapture.closeMapCapture(this);

    loadMarkerImages = () =>
        Promise.all(
            ICON_NAMES.map(
                (name) =>
                    new Promise((resolve) => {
                        if (this.map.hasImage(name)) return resolve();
                        this.map.loadImage(`/img/markers/${name}.png`, (err, image) => {
                            if (err || !image) return resolve();
                            if (!this.map.hasImage(name)) this.map.addImage(name, image);
                            resolve();
                        });
                    })
            )
        );

    // Carrega os 8 marcadores coloridos do mapa de calor sob nomes heat_{HEX}.
    loadHeatMarkerImages = () =>
        Promise.all(
            HEAT_COLORS.map(
                (hex) =>
                    new Promise((resolve) => {
                        const name = `heat_${hex}`;
                        if (this.map.hasImage(name)) return resolve();
                        this.map.loadImage(`/img/marcadores/mapa_de_calor/${hex}.png`, (err, image) => {
                            if (err || !image) return resolve();
                            if (!this.map.hasImage(name)) this.map.addImage(name, image);
                            resolve();
                        });
                    })
            )
        );

    /**
     * Paint da camada de calor a partir dos ajustes do usuário.
     *
     * Raio e intensidade seguem a forma do exemplo aprovado: o valor escolhido vale no
     * zoom 10 e dobra no 15. Sem essa rampa por zoom o calor encolhe visualmente ao
     * aproximar, porque o raio é em pixels de tela e a mesma área passa a ocupar mais.
     *
     * @param {{ramp: string, radius: number, intensity: number, opacity: number}} style
     */
    heatPaintFromStyle = ({ ramp, radius, intensity, opacity }) => ({
        'heatmap-color': HEAT_COLOR_RAMPS[ramp] || HEAT_COLOR_RAMPS[HEAT_STYLE_DEFAULT.ramp],
        'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 10, radius, 15, radius * 2],
        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 10, intensity, 15, intensity * 2],
        'heatmap-opacity': opacity,
    });

    applyHeatStyle = (style) => {
        if (!this.map?.getLayer('heatmap-layer')) return;

        Object.entries(this.heatPaintFromStyle(style)).forEach(([prop, value]) => {
            this.map.setPaintProperty('heatmap-layer', prop, value);
        });
    };

    /**
     * Handler dos controles de personalização: aplica direto na camada, sem refazer a
     * classificação por percentis (que depende só da métrica, não da aparência).
     * Espelha no `heatMapConfig` para a troca de estilo do mapa reconstruir igual.
     */
    updateHeatStyle = (patch) => {
        const style = { ...this.state.heatMapStyle, ...patch };

        this.setState({ heatMapStyle: style });
        this.applyHeatStyle(style);

        if (this.heatMapConfig) {
            this.heatMapConfig = { ...this.heatMapConfig, style };
        }
    };

    // Fonte dedicada (sem cluster) + camadas de calor. Iniciam ocultas; addHeatMap
    // as popula e exibe. Mantê-las separadas evita conflito com o clustering da
    // fonte principal e permite pesos/recoloração por ponto.
    setupHeatLayers = () => {
        if (this.map.getSource(HEAT_SOURCE_ID)) return;

        this.map.addSource(HEAT_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });

        this.map.addLayer({
            id: 'heatmap-layer',
            type: 'heatmap',
            source: HEAT_SOURCE_ID,
            layout: { visibility: 'none' },
            paint: {
                'heatmap-weight': 1,
                ...this.heatPaintFromStyle(this.state.heatMapStyle),
            },
        });

        this.map.addLayer({
            id: 'heat-points',
            type: 'symbol',
            source: HEAT_SOURCE_ID,
            layout: {
                visibility: 'none',
                'icon-image': ['get', 'heatIcon'],
                'icon-size': MARKER_ICON_SIZE,
                'icon-allow-overlap': true,
                'icon-anchor': 'bottom',
            },
        });
    };

    // Constrói a config da legenda (8 faixas) espelhando getHeatMapWeights de DefaulMap.jsx.
    buildHeatLegendConfig = (percentis, metric) => {
        const isCurrency = metric === 'vgv_final' || metric === 'ticket_medio';
        const increment = ['oferta_final', 'oferta_inicial'].includes(metric) ? 1 : 0.1;
        const fmt = (v) =>
            isCurrency ? Number(v.toFixed(2)).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : v;

        const config = [];
        let prev = 0;
        percentis.forEach((p, index) => {
            const min = index === 0 ? 0 : percentis[prev] + increment;
            config.push({
                min: fmt(min),
                max: fmt(p),
                icon: `/img/marcadores/mapa_de_calor/${HEAT_COLORS[index]}.png`,
            });
            prev = index;
        });

        config.push({
            min: fmt(percentis[percentis.length - 1] + increment),
            max: undefined,
            icon: `/img/marcadores/mapa_de_calor/${HEAT_COLORS[HEAT_COLORS.length - 1]}.png`,
        });

        return config;
    };

    // Retorna o índice do bucket (0..7) de um valor conforme os percentis.
    heatBucketIndex = (value, percentis) => {
        if (value <= percentis[0]) return 0;
        if (value > percentis[6]) return 7;
        for (let i = 1; i <= 6; i++) {
            if (value > percentis[i - 1] && value <= percentis[i]) return i;
        }
        return 7;
    };

    addHeatMap = (config) => {
        if (!this.map?.getSource(HEAT_SOURCE_ID) || this.insideFeatures.length === 0) return;

        const metric = config.type?.value;
        if (!metric) return;

        // Ajustes vêm do modal na criação; na reconstrução (troca de estilo do mapa) já
        // chegam no config salvo. Sem nenhum dos dois, cai nos defaults aprovados.
        const style = { ...HEAT_STYLE_DEFAULT, ...(config.style || {}) };

        this.heatMapConfig = { ...config, style };

        // O calor é derivado do recorte das formas — os marcadores de fora, exibidos
        // apenas como referência, ficariam de fora dos percentis.
        let features = this.insideFeatures;

        // Para "Lançamentos", filtra pelo intervalo de datas escolhido.
        if (metric === 'oferta_inicial' && config.data_inicial && config.data_final) {
            const start = new Date(config.data_inicial);
            const end = new Date(config.data_final);
            features = features.filter((f) => {
                const d = f.properties?.data_lancamento;
                if (!d) return false;
                const date = new Date(d);
                return date >= start && date <= end;
            });
        }

        const values = features.map((f) => Number(f.properties?.[metric]) || 0);
        if (features.length === 0) {
            toast.info('Nenhum empreendimento com dados para a métrica selecionada.');
            return;
        }

        const rows = percentile(HEAT_PERCENTILES, features, (f) => Number(f.properties?.[metric]) || 0);
        const percentis = rows.map((r) => Number(r.properties?.[metric]) || 0);

        const heatFeatures = features.map((f) => {
            const value = Number(f.properties?.[metric]) || 0;
            const bucket = this.heatBucketIndex(value, percentis);
            return {
                ...f,
                properties: {
                    ...f.properties,
                    heatWeight: value,
                    heatIcon: HEAT_ICON_NAMES[bucket],
                },
            };
        });

        this.map.getSource(HEAT_SOURCE_ID).setData({ type: 'FeatureCollection', features: heatFeatures });

        // heatmap-weight: normaliza o valor da métrica entre min e max reais.
        const min = Math.min(...values);
        const max = Math.max(...values);
        if (max > min) {
            this.map.setPaintProperty('heatmap-layer', 'heatmap-weight', [
                'interpolate', ['linear'], ['get', 'heatWeight'], min, 0, max, 1,
            ]);
        } else {
            this.map.setPaintProperty('heatmap-layer', 'heatmap-weight', 1);
        }

        this.map.setLayoutProperty('heatmap-layer', 'visibility', 'visible');
        // `heat-points` são os próprios empreendimentos (um ícone por ponto, colorido pela
        // faixa): entram respeitando o switch Empreendimentos, não sempre visíveis.
        this.setHeatPointsVisibility(this.state.showEmpreendimentos);
        CLUSTER_LAYERS.forEach((id) => this.map.setLayoutProperty(id, 'visibility', 'none'));

        this.applyHeatStyle(style);

        this.setState({
            showHeatMap: true,
            heatMapWeightType: metric,
            heatMapStyle: style,
            heatMapLegendConfig: this.buildHeatLegendConfig(percentis, metric),
            active_tab: 'legendas',
        });
    };

    removeHeatMap = () => {
        this.heatMapConfig = null;
        if (this.map?.getSource(HEAT_SOURCE_ID)) {
            this.map.getSource(HEAT_SOURCE_ID).setData(EMPTY_FC);
            HEAT_LAYERS.forEach((id) => this.map.setLayoutProperty(id, 'visibility', 'none'));
            // Restaura os marcadores respeitando a preferência de exibição.
            this.setEmpreendimentosVisibility(this.state.showEmpreendimentos);
        }
        this.setState({
            showHeatMap: false,
            heatMapWeightType: null,
            heatMapLegendConfig: [],
            heatMapStyle: { ...HEAT_STYLE_DEFAULT },
        });
    };

    setupClusterLayers = () => {
        this.map.addSource(SOURCE_ID, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
            // Sem clusterização, nenhuma feature ganha `point_count` e as duas camadas
            // de cluster (que filtram por essa propriedade) simplesmente não desenham.
            cluster: this.state.clusterMarkers,
            clusterMaxZoom: 14,
            clusterRadius: 50,
            // Quantos pontos do cluster caem dentro das formas desenhadas — um cluster
            // com zero é inteiramente "de fora" e aparece esmaecido.
            clusterProperties: INSIDE_CLUSTER_PROPERTIES,
        });

        this.map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: SOURCE_ID,
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': ['step', ['get', 'point_count'], '#51bbd6', 50, '#f1f075', 200, '#f28cb1'],
                'circle-radius': ['step', ['get', 'point_count'], 18, 50, 24, 200, 30],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
                'circle-opacity': CLUSTER_OUTSIDE_OPACITY,
                'circle-stroke-opacity': CLUSTER_OUTSIDE_OPACITY,
            },
        });

        this.map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['has', 'point_count'],
            layout: {
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 13,
            },
            paint: { 'text-opacity': CLUSTER_OUTSIDE_OPACITY },
        });

        this.map.addLayer({
            id: 'unclustered-point',
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['!', ['has', 'point_count']],
            layout: {
                // range_icon (marcador por faixa de valor — ModalRanges) tem prioridade
                // sobre o ícone padrão por status/padrão.
                'icon-image': ['coalesce', ['get', 'range_icon'], ['get', 'icon']],
                'icon-size': MARKER_ICON_SIZE,
                'icon-allow-overlap': true,
                'icon-anchor': 'bottom',
            },
            paint: { 'icon-opacity': POINT_OUTSIDE_OPACITY },
        });

        this.bindClusterEvents();
    };

    /**
     * Handlers das camadas de empreendimento. Ficam fora de setupClusterLayers porque
     * ela roda de novo a cada troca de estilo e a cada troca do switch de agrupamento —
     * e o Mapbox delega o evento pelo id da camada, então uma única assinatura continua
     * valendo para as camadas recriadas (registrar de novo só duplicaria o handler).
     */
    /**
     * Conteúdo do popup do marcador de empreendimento, no layout aprovado internamente
     * para este chamado: título, incorporadora (quando houver), tipo/padrão, estoque e
     * preço médio. "Preço Médio" sai de `ticket_medio` (bases.avg_ticket), que é o campo
     * com esse significado no schema — o mesmo exibido na ficha do empreendimento.
     *
     * @param {Record<string, unknown>} p `properties` da feature do empreendimento.
     * @returns {string} HTML do popup.
     */
    empreendimentoPopupHtml = (p) => {
        const estoque = Number(p.oferta_final);
        const precoMedio = Number(p.ticket_medio);

        const linhas = [
            p.incorporadora
                ? `<div class="popup-row">Incorporadora: <strong>${escapeHtml(p.incorporadora)}</strong></div>`
                : '',
            `<div class="popup-row">Tipo: <strong>${escapeHtml(p.type)}</strong> | Padrão: <strong>${escapeHtml(p.padrao)}</strong></div>`,
            `<div class="popup-row">Estoque: <strong>${Number.isFinite(estoque) ? estoque.toLocaleString('pt-BR') : 'N/D'}</strong></div>`,
            `<div class="popup-row">Preço Médio: <strong>${precoMedio > 0 ? BRL_SEM_CENTAVOS.format(precoMedio) : 'N/D'}</strong></div>`,
        ];

        return `<div class="popup-title">${escapeHtml(p.name)}</div>${linhas.join('')}`;
    };

    bindClusterEvents = () => {
        if (this.clusterEventsBound) return;
        this.clusterEventsBound = true;

        this.map.on('click', 'clusters', (e) => {
            const features = this.map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
            const clusterId = features[0].properties.cluster_id;
            this.map.getSource(SOURCE_ID).getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return;
                this.map.easeTo({ center: features[0].geometry.coordinates, zoom });
            });
        });

        const hoverPopup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 12,
            // Acima do padrão (240px) para o pior caso de "Tipo: X | Padrão: Y"
            // ("Comercial | Loteamento Fechado") caber numa linha só, com folga para
            // fallback de fonte. Nome e incorporadora longos quebram em mais linhas,
            // em vez de vazarem (ver .popup-title/.popup-row em mapa.css).
            maxWidth: '300px',
            // Estilo compartilhado dos popups dos mapas novos (ver .popup-mapa em mapa.css).
            className: 'popup-mapa',
        });

        this.map.on('mouseenter', 'unclustered-point', (e) => {
            this.map.getCanvas().style.cursor = 'pointer';
            const f = e.features[0];
            const coords = f.geometry.coordinates.slice();

            hoverPopup.setLngLat(coords).setHTML(this.empreendimentoPopupHtml(f.properties)).addTo(this.map);
        });

        this.map.on('mouseleave', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = '';
            hoverPopup.remove();
        });

        // Clique num marcador individual → abre o modal de detalhes do empreendimento.
        this.map.on('click', 'unclustered-point', (e) => {
            const f = e.features?.[0];
            if (!f) return;
            hoverPopup.remove();
            this.openEmpreendimentoModal(f.properties?.id);
        });

        this.map.on('mouseenter', 'clusters', () => { this.map.getCanvas().style.cursor = 'pointer'; });
        this.map.on('mouseleave', 'clusters', () => { this.map.getCanvas().style.cursor = ''; });
    };

    // ===== Polos geradores =====

    // Carrega os 11 ícones de categoria sob o nome `polo_<arquivo>`.
    loadPolosImages = () =>
        Promise.all(
            POLO_ICON_FILES.map(
                (file) =>
                    new Promise((resolve) => {
                        const name = `polo_${file}`;
                        if (this.map.hasImage(name)) return resolve();
                        this.map.loadImage(`/img/polos_geradores/grupos/${file}.png`, (err, image) => {
                            if (err || !image) return resolve();
                            // pixelRatio: 2 → o PNG de 64px é tratado como ícone de 32px @2x
                            // (nítido em telas retina, sem serrilhar por minificação).
                            if (!this.map.hasImage(name)) this.map.addImage(name, image, { pixelRatio: 2 });
                            resolve();
                        });
                    })
            )
        );

    /**
     * Cor do cluster da categoria, espelhando `0.4 + intensity * 0.6` (maxCount 100) do
     * ClusterService do mapa Google. A intensidade vai no alpha da própria cor, e não em
     * `circle-opacity`, que já é usado por CLUSTER_OUTSIDE_OPACITY para esmaecer o que
     * está fora das formas desenhadas.
     */
    poloClusterColor = (category) => {
        const [r, g, b] = polosGeradoresColor[category] || polosGeradoresColor[POLO_CATEGORY_FALLBACK];

        return [
            'interpolate', ['linear'], ['get', 'point_count'],
            0, `rgba(${r}, ${g}, ${b}, 0.4)`,
            100, `rgba(${r}, ${g}, ${b}, 1)`,
        ];
    };

    setupPolosLayers = () => {
        POLO_CATEGORIES.forEach((category) => {
            const sourceId = polosSourceId(category);
            if (this.map.getSource(sourceId)) return;

            this.map.addSource(sourceId, {
                type: 'geojson',
                data: EMPTY_FC,
                // Mesmo switch de agrupamento dos empreendimentos (toggleClusterMarkers).
                cluster: this.state.clusterMarkers,
                clusterMaxZoom: 14,
                clusterRadius: 50,
                clusterProperties: INSIDE_CLUSTER_PROPERTIES,
            });

            this.map.addLayer({
                id: polosClusterLayerId(category),
                type: 'circle',
                source: sourceId,
                filter: ['has', 'point_count'],
                layout: { visibility: 'none' },
                paint: {
                    'circle-color': this.poloClusterColor(category),
                    'circle-radius': ['step', ['get', 'point_count'], 16, 50, 22, 200, 28],
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#ffffff',
                    'circle-opacity': CLUSTER_OUTSIDE_OPACITY,
                    'circle-stroke-opacity': CLUSTER_OUTSIDE_OPACITY,
                },
            });

            this.map.addLayer({
                id: polosCountLayerId(category),
                type: 'symbol',
                source: sourceId,
                filter: ['has', 'point_count'],
                layout: {
                    visibility: 'none',
                    'text-field': ['get', 'point_count_abbreviated'],
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 13,
                },
                paint: { 'text-color': '#ffffff', 'text-opacity': CLUSTER_OUTSIDE_OPACITY },
            });

            this.map.addLayer({
                id: polosPointLayerId(category),
                type: 'symbol',
                source: sourceId,
                filter: ['!', ['has', 'point_count']],
                layout: {
                    visibility: 'none',
                    'icon-image': ['get', 'icon'],
                    // Imagens registradas com pixelRatio 2 (64px = 32px @2x) → icon-size 1.
                    'icon-size': 1,
                    'icon-allow-overlap': true,
                    'icon-anchor': 'bottom',
                },
                paint: { 'icon-opacity': POINT_OUTSIDE_OPACITY },
            });
        });

        this.bindPolosEvents();
    };

    /**
     * Handlers das camadas de polos geradores. Ficam fora de setupPolosLayers pelo mesmo
     * motivo de bindClusterEvents: ela roda de novo a cada troca de estilo e a cada troca
     * do switch de agrupamento, e o Mapbox delega o evento pelo id da camada — registrar
     * de novo só duplicaria o handler.
     */
    /**
     * Conteúdo do popup do polo gerador, na mesma estrutura do popup de empreendimento
     * (`.popup-title` + `.popup-row`), para que os dois fiquem idênticos no mapa.
     *
     * @param {Record<string, unknown>} p `properties` da feature do polo.
     * @returns {string} HTML do popup.
     */
    poloPopupHtml = (p) => {
        const endereco = p.address && p.address.length ? p.address : 'Endereço não disponível';

        return `<div class="popup-title">${escapeHtml(p.title || '')}</div>`
            + `<div class="popup-row">${escapeHtml(endereco)}</div>`;
    };

    bindPolosEvents = () => {
        if (this.polosEventsBound) return;
        this.polosEventsBound = true;

        // Mesmo balão do popup de empreendimento (ver .popup-mapa em mapa.css).
        const poloPopup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 12,
            maxWidth: '300px',
            className: 'popup-mapa',
        });

        /*
         * As camadas de polo agora são uma por categoria, e ícones/clusters de categorias
         * diferentes podem se sobrepor (icon-allow-overlap). O Mapbox dispara os eventos por
         * camada, sem ordem garantida entre elas: ao passar direto de um polo para outro de
         * outra categoria, o `mouseleave` da primeira pode chegar depois do `mouseenter` da
         * segunda e apagar o popup/cursor recém-abertos. Guardar quais camadas estão sob o
         * cursor resolve as duas ordens.
         */
        const camadasSobCursor = new Set();

        const entrouEm = (layer) => {
            camadasSobCursor.add(layer);
            this.map.getCanvas().style.cursor = 'pointer';
        };

        const saiuDe = (layer) => {
            camadasSobCursor.delete(layer);
            if (camadasSobCursor.size === 0) {
                this.map.getCanvas().style.cursor = '';
                poloPopup.remove();
            }
        };

        POLO_CATEGORIES.forEach((category) => {
            const sourceId = polosSourceId(category);
            const clusterLayer = polosClusterLayerId(category);
            const pointLayer = polosPointLayerId(category);

            this.map.on('click', clusterLayer, (e) => {
                // Clusters de categorias sobrepostas fazem o mesmo clique cair em mais de uma
                // camada; sem isso, dois easeTo concorrentes para centros diferentes.
                if (this.ultimoCliqueClusterPolo === e.originalEvent) return;
                this.ultimoCliqueClusterPolo = e.originalEvent;

                const features = this.map.queryRenderedFeatures(e.point, { layers: [clusterLayer] });
                const clusterId = features[0].properties.cluster_id;
                this.map.getSource(sourceId).getClusterExpansionZoom(clusterId, (err, zoom) => {
                    if (err) return;
                    this.map.easeTo({ center: features[0].geometry.coordinates, zoom });
                });
            });

            this.map.on('mouseenter', pointLayer, (e) => {
                entrouEm(pointLayer);
                const f = e.features[0];
                const coords = f.geometry.coordinates.slice();
                poloPopup
                    .setLngLat(coords)
                    .setHTML(this.poloPopupHtml(f.properties))
                    .addTo(this.map);
            });

            this.map.on('mouseleave', pointLayer, () => saiuDe(pointLayer));
            this.map.on('mouseenter', clusterLayer, () => entrouEm(clusterLayer));
            this.map.on('mouseleave', clusterLayer, () => saiuDe(clusterLayer));
        });
    };

    /**
     * Anéis externos das formas desenhadas, em [lng, lat] — o recorte geográfico que vai
     * ao backend junto com as cidades.
     *
     * @returns {Array<Array<[number, number]>>}
     */
    polosPolygonsDoDraw = () => {
        const aneis = [];

        (this.draw?.getAll().features || []).forEach((f) => {
            if (f.geometry?.type === 'Polygon') {
                aneis.push(f.geometry.coordinates?.[0]);
            } else if (f.geometry?.type === 'MultiPolygon') {
                (f.geometry.coordinates || []).forEach((poly) => aneis.push(poly?.[0]));
            }
        });

        return aneis.filter((ring) => Array.isArray(ring) && ring.length >= 4);
    };

    // Busca os polos das cidades selecionadas e popula o cache `allPolos`.
    // `types` é passado explicitamente para evitar corrida com o setState da seleção.
    //
    // As formas desenhadas vão junto: o filtro por cidade sozinho perde os polos de
    // municípios vizinhos que caem dentro da área (e os que têm `city` divergente na
    // base) — o mapa Google, ao desenhar, consultava só por geometria. O backend une
    // os dois escopos, então a cidade segue dando o contexto esmaecido em volta.
    fetchPolos = async (cidades, types = this.state.polosTypesSelected, polygons = this.polosPolygonsDoDraw()) => {
        // A camada só carrega depois que o usuário escolhe as categorias (ver togglePolos).
        if (!types || types.length === 0) return;

        const city = [];
        const state = [];
        (cidades || []).forEach((c) => {
            const parsed = parseCidadeValue(c.value);
            if (!parsed) return;
            city.push(parsed.cidade);
            if (!state.includes(parsed.uf)) state.push(parsed.uf);
        });

        // Assinatura das formas já consideradas (ver refreshPolosPorArea) — gravada mesmo
        // quando não há o que buscar, senão toda mudança de forma tentaria de novo.
        this._polosShapesKey = JSON.stringify(polygons);

        if (city.length === 0 && polygons.length === 0) return;

        const payload = {};
        if (city.length > 0) {
            payload.city = city;
            payload.state = state;
        }
        if (polygons.length > 0) payload.polygons = polygons;

        // Com todas as categorias marcadas o filtro não vai ao backend: assim a busca
        // também traz os polos cuja `main_category` está fora da lista padrão (parte da
        // base não está normalizada), como fazia antes de a seleção virar obrigatória.
        if (types.length < POLO_CATEGORIES.length) payload.types = types;

        // Ignora a resposta de uma busca que já foi substituída por outra (o usuário
        // mexe na forma mais rápido do que a consulta responde numa cidade grande).
        const requisicao = ++this._polosRequestId;

        this.setState({ polosLoading: true });

        try {
            // Endpoint enxuto do mapa novo: já devolve GeoJSON pronto (com `icon`).
            // Cidades grandes levam dezenas de segundos na primeira busca (depois o
            // backend responde do cache), daí o timeout folgado.
            const res = await axios.post('/api/polos-geradores-mapbox', payload, { timeout: 180000 });
            if (requisicao !== this._polosRequestId) return;

            this.allPolos = res.data.features || [];

            this.setState({ polosLoaded: true, polosLoading: false });
            this.applyPolosShapeFilter();

            if (this.allPolos.length === 0) {
                toast.info('Nenhum polo gerador encontrado para os filtros selecionados.');
            }
        } catch (e) {
            if (requisicao !== this._polosRequestId) return;
            this.setState({ polosLoading: false });
            console.error('[MapboxMap] erro ao carregar polos geradores:', e);
            toast.error('Erro ao carregar polos geradores.');
        }
    };

    /**
     * Recorta os polos pelas formas desenhadas (se houver) e distribui as features entre
     * as sources por categoria. Também publica em `polosCategoriasCarregadas` as categorias
     * que ficaram com polos — é a fonte da legenda flutuante de gradiente, que só lista o
     * que está de fato no mapa (mesma regra do `markersByCategory` do mapa Google).
     */
    applyPolosShapeFilter = () => {
        if (!this.map?.getSource(polosSourceId(POLO_CATEGORY_FALLBACK))) return;

        const shapes = (this.draw?.getAll().features || []).filter(
            (f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
        );

        // Mesma regra dos empreendimentos: os polos de fora seguem no mapa, esmaecidos.
        markFeaturesInsideShapes(this.allPolos, shapes);

        const porCategoria = new Map(POLO_CATEGORIES.map((category) => [category, []]));
        this.allPolos.forEach((feature) => {
            const category = feature.properties?.main_category;
            const bucket = porCategoria.get(category) || porCategoria.get(POLO_CATEGORY_FALLBACK);
            bucket.push(feature);
        });

        const categoriasCarregadas = [];
        porCategoria.forEach((features, category) => {
            this.map.getSource(polosSourceId(category))?.setData({ type: 'FeatureCollection', features });
            if (features.length > 0) categoriasCarregadas.push(category);
        });

        this.setState({ polosCategoriasCarregadas: categoriasCarregadas });
    };

    setPolosLayersVisibility = (visible) => {
        const value = visible ? 'visible' : 'none';
        POLOS_LAYERS.forEach((id) => {
            if (this.map?.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', value);
        });
    };

    // ===== Exibir/esconder marcadores de empreendimentos =====
    setEmpreendimentosVisibility = (visible) => {
        const value = visible ? 'visible' : 'none';
        CLUSTER_LAYERS.forEach((id) => {
            if (this.map?.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', value);
        });
    };

    /** Ícones por empreendimento desenhados sobre o calor (fonte separada do cluster). */
    setHeatPointsVisibility = (visible) => {
        if (this.map?.getLayer('heat-points')) {
            this.map.setLayoutProperty('heat-points', 'visibility', visible ? 'visible' : 'none');
        }
    };

    /**
     * Liga/desliga o agrupamento dos marcadores — empreendimentos e polos geradores, que
     * têm sources separadas. `cluster` é opção da source do Mapbox e não pode ser trocada
     * numa source existente, então cada source e suas camadas são recriadas com os dados
     * já carregados — sem refazer a busca.
     */
    toggleClusterMarkers = (enabled) => {
        this.setState({ clusterMarkers: enabled }, () => {
            if (!this.map) return;

            if (this.map.getSource(SOURCE_ID)) {
                CLUSTER_LAYERS.forEach((id) => {
                    if (this.map.getLayer(id)) this.map.removeLayer(id);
                });
                this.map.removeSource(SOURCE_ID);

                this.setupClusterLayers();
                this.map.getSource(SOURCE_ID).setData(this.currentGeojson || EMPTY_FC);
                // Com o mapa de calor ativo os marcadores ficam ocultos por design.
                this.setEmpreendimentosVisibility(!this.state.showHeatMap && this.state.showEmpreendimentos);
            }

            if (this.map.getSource(polosSourceId(POLO_CATEGORY_FALLBACK))) {
                POLOS_LAYERS.forEach((id) => {
                    if (this.map.getLayer(id)) this.map.removeLayer(id);
                });
                POLO_CATEGORIES.forEach((category) => {
                    const sourceId = polosSourceId(category);
                    if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
                });

                this.setupPolosLayers();
                // Repõe os polos já buscados (com a marcação de dentro/fora das formas).
                this.applyPolosShapeFilter();
                this.setPolosLayersVisibility(this.state.showPolosGeradores);
            }

            // As camadas recriadas entraram no topo da pilha: devolve os desenhos para cima delas.
            this.reordenarCamadas();
        });
    };

    toggleEmpreendimentos = (enabled) => {
        this.setState({ showEmpreendimentos: enabled });

        /*
         * Com o mapa de calor ativo as camadas de cluster já estão ocultas por design, e
         * quem desenha os empreendimentos é `heat-points` — é ela que o switch precisa
         * apagar, senão o botão não surtia efeito nenhum sobre o calor. A preferência é
         * reaplicada nas camadas de cluster ao remover o heatmap (removeHeatMap).
         */
        if (this.state.showHeatMap) {
            this.setHeatPointsVisibility(enabled);
            return;
        }

        this.setEmpreendimentosVisibility(enabled);
    };

    togglePolos = async (enabled) => {
        this.setState({ showPolosGeradores: enabled });
        this.setPolosLayersVisibility(enabled);

        if (!enabled) return;

        // Nada é carregado antes da escolha das categorias: uma cidade inteira são
        // dezenas de milhares de polos, e é a seleção que torna a busca utilizável.
        if (this.state.polosTypesSelected.length === 0) {
            this.setState({ polosModalOpen: true });
            return;
        }

        if (this.allPolos.length === 0) {
            await this.buscarPolos(this.state.polosTypesSelected);
        }
    };

    /**
     * Dispara a busca de polos com as categorias escolhidas, exigindo um recorte —
     * cidade buscada ou área desenhada. Ponto único de entrada de togglePolos e
     * applyPolosTypes.
     */
    buscarPolos = async (types) => {
        const cidades = this.cidadesDosFiltros();

        // Com uma área desenhada a geometria basta — não exige cidade selecionada.
        if (cidades.length === 0 && this.polosPolygonsDoDraw().length === 0) {
            toast.info('Selecione e busque uma cidade (ou desenhe uma área) para carregar os polos geradores.');
            return;
        }

        await this.fetchPolos(cidades, types);
    };

    /** Tira os polos do mapa sem desligar a camada (usado ao limpar as categorias). */
    limparPolosDoMapa = () => {
        this.allPolos = [];
        this._polosShapesKey = '';
        this._polosRequestId++;
        POLO_CATEGORIES.forEach((category) => {
            this.map?.getSource(polosSourceId(category))?.setData(EMPTY_FC);
        });
        this.setState({ polosCategoriasCarregadas: [], polosLoaded: false, polosLoading: false });
    };

    /** Cidades do form — e as da última busca quando a sidebar está fechada e o form desmontou. */
    cidadesDosFiltros = () =>
        this.formFiltersRef.current?.getFilters?.()?.cidades || this.ultimosFiltros?.cidades || [];

    /**
     * Refaz a busca de polos quando as formas desenhadas mudam (criar, mover,
     * redimensionar, ocultar, apagar, importar KML, carregar projeto). Roda a partir de
     * applyShapeFilter, que é chamado em todos esses caminhos — daí a guarda pela
     * assinatura das formas, para não refazer a busca quando só os empreendimentos
     * mudaram.
     */
    refreshPolosPorArea = () => {
        if (!this.state.showPolosGeradores || this.state.polosTypesSelected.length === 0) return;

        const polygons = this.polosPolygonsDoDraw();
        const assinatura = JSON.stringify(polygons);
        if (assinatura === this._polosShapesKey) return;

        this.fetchPolos(this.cidadesDosFiltros(), this.state.polosTypesSelected, polygons);
    };

    // ===== Resumo dos filtros da busca (badges no card de cada área) =====

    // Monta os badges com os filtros ativos no form no momento da busca/desenho —
    // paridade com o meta_data.request do InfoCard do mapa Google, mas montado no
    // cliente (o form já tem os labels de cidades/incorporadoras/empreendimentos).
    buildFilterBadges = (filters) => {
        const badges = [];
        const push = (v) => { if (v !== null && v !== undefined && v !== '') badges.push(String(v)); };

        const cidades = filters.cidades || [];
        cidades.forEach((c) => push(c.label || c.value));
        if (cidades.length === 0) (filters.estados || []).forEach(push);

        const status = filters.status?.length ? filters.status : ['Ativo'];
        status.forEach(push);

        (filters.tipo || []).forEach(push);
        (filters.padrao || []).forEach(push);
        (filters.bairros || []).forEach(push);
        (filters.incorporadoras || []).forEach((i) => push(i.label || i.value));
        (filters.nome_empreendimento || []).forEach((n) => push(n.label || n.value));
        (filters.estagio || []).forEach(push);
        (filters.retrofit || []).forEach((r) => push(Number(r) === 1 ? 'Retrofit' : 'Sem retrofit'));

        if (filters.quartos?.length) push(`Quartos: ${filters.quartos.join(', ')}`);
        if (filters.ano_lancamento?.length) push(`Lançamento: ${filters.ano_lancamento.join(', ')}`);
        if (filters.mes_ano_lancamento instanceof Date) {
            const d = filters.mes_ano_lancamento;
            push(`Lançamento: ${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`);
        }

        const faixas = [
            ['Área ≥', filters.area_maior_que], ['Área ≤', filters.area_menor_que],
            ['Valor ≥', filters.valor_maior_que], ['Valor ≤', filters.valor_menor_que],
            ['Valor m² ≥', filters.valor_metro_maior_que], ['Valor m² ≤', filters.valor_metro_menor_que],
            ['% vendida ≥', filters.porcentagem_vendida_minima], ['% vendida ≤', filters.porcentagem_vendida_maxima],
            ['Juros ≥', filters.juros_minimo], ['Juros ≤', filters.juros_maximo],
            ['Parcelas ≥', filters.parcelas_minimo], ['Parcelas ≤', filters.parcelas_maximo],
            ['Parcela ≥', filters.valor_parcela_minima], ['Parcela ≤', filters.valor_parcela_maxima],
        ];
        faixas.forEach(([label, valor]) => {
            if (valor !== '' && valor !== null && valor !== undefined && Number(valor) !== 0) {
                push(`${label} ${valor}`);
            }
        });

        return badges;
    };

    currentFilterBadges = () =>
        this.buildFilterBadges(this.formFiltersRef.current?.getFilters?.() || {});

    // ===== Comparar Áreas / Comparar Polos (SPECs 12 e 13) =====

    baixarBlob = (data, filename) => {
        const url = URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    // Áreas analisadas elegíveis (com snapshot da busca no Redis).
    compareAreasOptions = () =>
        this.state.searchAreas
            .filter((a) => a.meta_data?.cache_key)
            .map((a) => ({ label: a.name, value: { label: a.name, cache_key: a.meta_data.cache_key } }));

    handleCompareAreas = async (values) => {
        try {
            const res = await axios.post(
                '/api/empreendimentos-mapbox/compare-areas',
                { areas: values },
                { responseType: 'blob' }
            );
            this.baixarBlob(res.data, 'comparar_areas.xlsx');
        } catch (e) {
            console.error('[MapboxMap] erro ao comparar áreas:', e);
            if (e.response?.status === 404) {
                toast.warn('Análise expirada. Refaça a busca no mapa.');
            } else {
                toast.error('Erro ao gerar a comparação de áreas.');
            }
        }
    };

    /**
     * Polos dentro de uma área do painel de resultados.
     *
     * Áreas desenhadas recortam pela forma; a área da "busca completa" (sem forma) leva
     * todos os polos carregados. Recorte client-side, como o resto do fluxo de polos do
     * MapBox — é O(polos) por área e só roda no render do painel e ao abrir o modal.
     *
     * @param {{id: string, isDrawnArea?: boolean}} area
     * @returns {Array<object>} Features de polo dentro da área.
     */
    polosDaArea = (area) => {
        if (!this.allPolos.length || !this.state.showPolosGeradores) return [];
        if (!area?.isDrawnArea) return this.allPolos;

        const shape = this.state.drawnShapes.find((s) => s.areaId === area.id);
        const feature = shape && (this.draw?.get(shape.drawId) || shape.feature);
        if (!feature || (feature.geometry?.type !== 'Polygon' && feature.geometry?.type !== 'MultiPolygon')) {
            return [];
        }

        return this.allPolos.filter((p) => {
            try {
                return booleanPointInPolygon(p, feature);
            } catch {
                return false;
            }
        });
    };

    abrirPolosDaArea = (area) => {
        this.setState({ polosDaAreaModal: { area, polos: this.polosDaArea(area) } });
    };

    /**
     * "Exportar" do modal de polos da área: manda as linhas exibidas para o endpoint do
     * MapBox, que devolve o XLSX (contagem por subcategoria + detalhamento). O recorte
     * por forma é client-side, então o servidor não teria como reproduzir a lista.
     */
    exportarPolosDaArea = async () => {
        const polos = this.state.polosDaAreaModal?.polos || [];
        if (!polos.length) return;

        try {
            const res = await axios.post(
                '/api/polos-geradores-mapbox/export',
                {
                    polos: polos.map((p) => ({
                        title: p.properties?.title || '',
                        address: p.properties?.address || '',
                        main_category: p.properties?.main_category || '',
                        sub_category: p.properties?.sub_category || '',
                        in_shopping: Boolean(p.properties?.in_shopping),
                        longitude: p.geometry?.coordinates?.[0] ?? null,
                        latitude: p.geometry?.coordinates?.[1] ?? null,
                    })),
                },
                { responseType: 'blob' }
            );
            this.baixarBlob(res.data, 'polos_geradores.xlsx');
        } catch (e) {
            console.error('[MapboxMap] erro ao exportar polos da área:', e);
            toast.error('Erro ao exportar os polos geradores.');
        }
    };

    contarCategoriasPolos = (features) =>
        features.reduce((acc, f) => {
            const cat = f.properties?.main_category || 'Outros';
            acc[cat] = (acc[cat] || 0) + 1;
            return acc;
        }, {});

    // Opções do Comparar Polos: a busca completa + cada forma desenhada (recorte
    // client-side, como o restante do fluxo de polos do MapBox). Calculado só na
    // abertura do modal — é O(formas × polos).
    comparePolosOptions = () => {
        if (!this.allPolos.length) return [];

        const options = [{
            label: 'Busca completa',
            value: { label: 'Busca completa', categorias: this.contarCategoriasPolos(this.allPolos) },
        }];

        this.state.drawnShapes.forEach((s) => {
            const feature = this.draw?.get(s.drawId) || s.feature;
            if (!feature || (feature.geometry?.type !== 'Polygon' && feature.geometry?.type !== 'MultiPolygon')) {
                return;
            }
            const inside = this.allPolos.filter((p) => {
                try {
                    return booleanPointInPolygon(p, feature);
                } catch {
                    return false;
                }
            });
            const label = `${s.type} ${s.name}`;
            options.push({ label, value: { label, categorias: this.contarCategoriasPolos(inside) } });
        });

        return options;
    };

    handleComparePolos = async (values) => {
        try {
            const res = await axios.post(
                '/api/polos-geradores-mapbox/compare',
                { areas: values },
                { responseType: 'blob' }
            );
            this.baixarBlob(res.data, 'polos_geradores_compare.xlsx');
        } catch (e) {
            console.error('[MapboxMap] erro ao comparar polos:', e);
            toast.error('Erro ao gerar a comparação de polos.');
        }
    };

    applyPolosTypes = async (types) => {
        this.setState({ polosTypesSelected: types, polosModalOpen: false });

        if (!this.state.showPolosGeradores) return;

        // Voltar a nenhuma categoria esvazia o mapa: é o mesmo estado de quem acabou de
        // ligar a camada e ainda não escolheu nada.
        if (types.length === 0) {
            this.limparPolosDoMapa();
            toast.info('Selecione ao menos uma categoria para carregar os polos geradores.');
            return;
        }

        await this.buscarPolos(types);
    };

    setupGooglePlaces = () => {
        loadGoogleMapsPlaces()
            .then(() => {
                if (!this.searchInputRef.current) {
                    return;
                }

                const autocomplete = new window.google.maps.places.Autocomplete(this.searchInputRef.current, {
                    // `types` alimenta o marcador do endereço: ele só aparece para um ponto
                    // específico, nunca para uma cidade. Ver features/addressMarker.js.
                    fields: ['geometry', 'formatted_address', 'name', 'types'],
                    componentRestrictions: { country: 'br' },
                });
                autocomplete.addListener('place_changed', async () => {
                    const place = autocomplete.getPlace();
                    if (!place.geometry?.location) return;
                    const lng = place.geometry.location.lng();
                    const lat = place.geometry.location.lat();
                    this.map.flyTo({ center: [lng, lat], zoom: SELECTED_ZOOM, essential: true });
                    this.addressMarker = syncAddressMarker(this.map, this.addressMarker, place, lat, lng);
                    await this.loadFromAddress(lat, lng, [place]);
                });
                this.searchInputRef.current.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') e.preventDefault();
                });
            })
            .catch((err) => console.error('[MapboxMap] falha ao carregar Google Places:', err));
    };

    // Opção de cidade ({value, label}) a partir de coordenadas, via geocodificação
    // reversa — usada pela busca por endereço e ao carregar projeto sem cidade salva.
    cidadeDasCoordenadas = async (lat, lng, place) => {
        let reverse;
        try {
            reverse = await geolocationService.reverse(lat, lng, place);
        } catch {
            toast.error('Não foi possível identificar a localidade.');
            return null;
        }
        const uf = reverse?.uf;
        const cidade = reverse?.cidade;
        if (!uf || !cidade) {
            toast.warn('Localidade sem cidade/UF identificável.');
            return null;
        }

        return { value: `${uf}-${cidade}`, label: `${cidade} - ${uf}` };
    };

    loadFromAddress = async (lat, lng, place) => {
        const cidadeOption = await this.cidadeDasCoordenadas(lat, lng, place);
        if (!cidadeOption) return;

        this.setState({ cidadeSincronizada: [cidadeOption] });

        // Reaproveita os filtros atuais do formulário, forçando a cidade do endereço buscado
        const currentFilters = this.formFiltersRef.current?.getFilters?.() || {};
        const filters = { ...currentFilters, cidades: [cidadeOption] };

        const payload = this.buildFiltersPayload(filters);

        await Promise.all([
            this.fetchAndRender(payload),
            this.fetchAnalysis(payload, cidadeOption.label),
        ]);

        this.refreshBairrosAvailability([cidadeOption]);
        if (this.state.showBairros) {
            this.renderBairros([cidadeOption]);
        }
        this.refreshZoneamentoAvailability([cidadeOption]);
        this.loadYearsOptionsForLinkagesDataLayer([cidadeOption]);
    };

    buildFiltersPayload = (filters) => {
        // `cidades` pode faltar quando a busca vem do desenho e nenhuma cidade foi
        // identificada — nesse caso são os bounding boxes que delimitam a área.
        const { cidades = [], status, tipo, padrao, ...rest } = filters;

        const formatDate = (d) => {
            if (!d || !(d instanceof Date)) return null;
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${mm}/${yyyy}`;
        };

        const nonEmpty = (v) => v !== '' && v !== null && v !== undefined && v !== 0;

        // Empreendimento e incorporadora viajam como id do MySQL. Projetos salvos no mapa
        // Google guardavam o nome nesse lugar: descartamos o que não for id para a busca
        // inteira não falhar na validação por causa de um filtro legado.
        const ids = (list) => (list || []).map((item) => Number(item.value)).filter(Number.isInteger);

        const payload = {
            cidades: cidades.map((c) => c.value),
            // Busca por estado: as UFs só valem quando nenhuma cidade foi selecionada —
            // com cidade escolhida, o Estado serve apenas para filtrar o select de cidades.
            estados: cidades.length === 0 ? (rest.estados || []) : [],
            status: status?.length ? status : ['Ativo'],
            tipo: tipo || [],
            padrao: padrao || [],
            bairros: rest.bairros || [],
            incorporadoras: ids(rest.incorporadoras),
            nome_empreendimento: ids(rest.nome_empreendimento),
            quartos: (rest.quartos || [])
                .map((q) => (q === 'Studio' ? DORMITORIO_STUDIO : q))
                .filter(Number.isInteger),
            ano_lancamento: rest.ano_lancamento || [],
            estagio: rest.estagio || [],
            retrofit: rest.retrofit || [],
            check_incluir_monitoradas: this.state.incluirNaoMonitoradas,
        };

        const scalars = {
            area_maior_que: rest.area_maior_que,
            area_menor_que: rest.area_menor_que,
            valor_maior_que: rest.valor_maior_que,
            valor_menor_que: rest.valor_menor_que,
            valor_metro_maior_que: rest.valor_metro_maior_que,
            valor_metro_menor_que: rest.valor_metro_menor_que,
            porcentagem_vendida_minima: rest.porcentagem_vendida_minima,
            porcentagem_vendida_maxima: rest.porcentagem_vendida_maxima,
            mes_ano_lancamento: formatDate(rest.mes_ano_lancamento),
            juros_minimo: rest.juros_minimo,
            juros_maximo: rest.juros_maximo,
            parcelas_minimo: rest.parcelas_minimo,
            parcelas_maximo: rest.parcelas_maximo,
            valor_parcela_minima: rest.valor_parcela_minima,
            valor_parcela_maxima: rest.valor_parcela_maxima,
        };
        Object.entries(scalars).forEach(([key, val]) => {
            if (nonEmpty(val)) payload[key] = val;
        });

        const bboxes = this.bboxesDasFormasVisiveis();
        if (bboxes.length > 0) {
            payload.bboxes = bboxes;
        }

        return payload;
    };

    handleFiltersSubmit = async (filters) => {
        this.ultimosFiltros = filters;

        // Formas visíveis no mapa também recortam a busca: os marcadores já são
        // recortados por applyShapeFilter, então a análise tem que seguir o mesmo
        // recorte — uma análise por forma, no lugar da análise da cidade/estado.
        const activeShapes = this.state.drawnShapes.filter((s) => s.visible && this.draw?.get(s.drawId));

        // Sem cidade nem estado, são as formas desenhadas que delimitam a busca
        // (paridade com o mapa Google): quem cobre a área é o `bboxes` do payload, que
        // atravessa divisas de município. A cidade do centro de cada forma é resolvida
        // só para preencher o formulário e habilitar as camadas por cidade (bairros,
        // zoneamento, polos, dados censitários) — se não vier, a busca corre assim mesmo.
        if ((filters.cidades || []).length === 0 && (filters.estados || []).length === 0) {
            if (activeShapes.length === 0) {
                toast.warn('Selecione ao menos uma cidade ou um estado, ou desenhe uma área no mapa.');
                return;
            }

            const cidadesDasFormas = await this.cidadesDasFormas(activeShapes);
            if (cidadesDasFormas.length > 0) {
                // Sincroniza a cidade no formulário (prop observada por FormFiltersContainer).
                this.setState({ cidadeSincronizada: cidadesDasFormas });
                filters = { ...filters, cidades: cidadesDasFormas };
            }
        }

        const cidades = filters.cidades || [];
        const estados = filters.estados || [];
        const buscaPorEstado = cidades.length === 0 && estados.length > 0;

        const payload = this.buildFiltersPayload(filters);
        const areaName = cidades.length > 0
            ? cidades.map((c) => c.label || c.value).join(', ')
            : estados.join(', ');

        await Promise.all([
            activeShapes.length > 0
                // Depende de allFeatures já carregado/recortado → sequencial.
                ? this.fetchAndRender(payload).then(() => this.refreshShapeAnalyses(activeShapes))
                : Promise.all([this.fetchAndRender(payload), this.fetchAnalysis(payload, areaName)]),
            this.state.showPolosGeradores ? this.fetchPolos(cidades) : Promise.resolve(),
        ]);

        // Na busca por estado não há cidade/endereço para posicionar a câmera:
        // enquadra nos resultados carregados.
        if (buscaPorEstado) {
            this.fitBoundsToLoadedFeatures();
        }

        this.refreshBairrosAvailability(cidades);
        if (this.state.showBairros) {
            this.renderBairros(cidades);
        }
        this.refreshZoneamentoAvailability(cidades);
        // Cidade pode ter mudado: reaplica o zoneamento se estiver ligado.
        if (this.state.showZoneamento) {
            this.showZoneamentoLayer();
        }
        this.loadYearsOptionsForLinkagesDataLayer(cidades);
    };

    handleFiltersClear = () => {
        if (this.state.pickingIsochroneCenter) this.toggleIsochronePick();
        this.addressMarker = removeAddressMarker(this.addressMarker);
        this.currentGeojson = null;
        this.allFeatures = [];
        this.insideFeatures = [];
        this.removeHeatMap();
        this.draw?.deleteAll();
        if (this.map?.getSource(SOURCE_ID)) {
            this.map.getSource(SOURCE_ID).setData({ type: 'FeatureCollection', features: [] });
        }
        if (this.map?.getSource(BAIRROS_SOURCE_ID)) {
            this.map.getSource(BAIRROS_SOURCE_ID).setData(EMPTY_FC);
        }
        this.limparPolosDoMapa();
        this.setPolosLayersVisibility(false);
        this.setEmpreendimentosVisibility(true);
        const zoneamentoLimpo = zoneamento.resetZoneamento(this);
        const datalayerLimpo = datalayer.resetDatalayer(this);
        if (this.searchInputRef.current) {
            this.searchInputRef.current.value = '';
        }
        this.setState({ totalEmpreendimentos: 0, totalCarregados: 0, cidadeSincronizada: null, searchAreas: [], drawnShapes: [], right_panel_visible: false, empreendimentoDetail: null, showEmpreendimentoModal: false, showBairros: false, bairrosAvailable: false, showPolosGeradores: false, polosLoaded: false, polosLoading: false, polosTypesSelected: [], showEmpreendimentos: true, incluirNaoMonitoradas: false, ...zoneamentoLimpo, ...datalayerLimpo, ...isochrone.ISOCHRONE_INITIAL_STATE, ranges: [], modalRangesOpen: false, loadedProjectId: null, modalSalvarProjetoOpen: false });
    };

    handleRemoveArea = (areaId) => {
        this.setState((prev) => {
            const updated = prev.searchAreas.filter((a) => a.id !== areaId);
            return { searchAreas: updated, right_panel_visible: updated.length > 0 };
        });
    };

    // Ao selecionar um único estado no filtro, centraliza o mapa na capital (paridade com
    // o DefaulMap.centralizarMapaNaCapital do mapa Google). Com múltiplas UFs não há um
    // centro óbvio; o enquadramento acontece após a busca (fitBoundsToLoadedFeatures).
    centralizarMapaNaCapital = async (estados) => {
        if (!this.map || !estados || estados.length !== 1) return;

        try {
            const res = await axios.get(route('state.capital', { state: estados[0] }));
            const { lat, lng } = res.data || {};
            if (lat == null || lng == null) return;

            this.map.flyTo({ center: [Number(lng), Number(lat)], zoom: 12 });
        } catch (e) {
            console.error('[MapboxMap] erro ao centralizar na capital do estado:', e);
        }
    };

    // Enquadra o mapa nos empreendimentos dentro das formas (pós-recorte por formas).
    fitBoundsToLoadedFeatures = () => {
        const features = this.insideFeatures || [];
        if (!this.map || features.length === 0) return;

        const bounds = new mapboxgl.LngLatBounds();
        features.forEach((f) => {
            const coords = f.geometry?.coordinates;
            if (coords) bounds.extend(coords);
        });

        if (!bounds.isEmpty()) {
            this.map.fitBounds(bounds, { padding: 60, duration: 600 });
        }
    };

    fetchAndRender = async (payload) => {
        try {
            const res = await axios.post('/api/empreendimentos-mapbox', payload);
            const geojson = res.data;

            this.allFeatures = geojson.features || [];
            if (this.state.ranges.length > 0) {
                await this.ensureRangeImages();
                this.tagFeaturesWithRanges();
            }
            this.applyShapeFilter();

            // Toast só quando NÃO há resultados na tela — com resultados, os próprios
            // marcadores/painel são o feedback (pedido do usuário). A busca ter trazido
            // empreendimentos só fora das formas é avisado por refreshShapeAnalyses.
            if (this.allFeatures.length === 0) {
                toast.info('Nenhum empreendimento encontrado para os filtros selecionados.');
            }
        } catch (e) {
            console.error('[MapboxMap] erro:', e);
            toast.error('Erro ao carregar empreendimentos.');
        }
    };

    fetchAnalysis = async (payload, areaName) => {
        const color = colors[this.state.searchAreas.length % colors.length];
        const areaId = `area-${Date.now()}`;

        this.setState((prev) => ({
            // Análise mais recente entra no TOPO do painel de resultados.
            searchAreas: [{ id: areaId, name: areaName, color, loading: true, meta_data: null, filtros: this.currentFilterBadges() }, ...prev.searchAreas],
            right_panel_visible: true,
        }));

        try {
            const res = await axios.post('/api/empreendimentos-mapbox/analysis', payload);
            const { meta_data } = res.data;

            this.setState((prev) => ({
                searchAreas: prev.searchAreas.map((a) =>
                    a.id === areaId ? { ...a, meta_data, loading: false } : a
                ),
            }));
        } catch (e) {
            console.error('[MapboxMap] erro metadata:', e);
            toast.error('Erro ao carregar dados de análise.');
            this.setState((prev) => ({
                searchAreas: prev.searchAreas.filter((a) => a.id !== areaId),
            }));
        }
    };

    handleToggleBairros = (enabled) => {
        this.setState({ showBairros: enabled });
        if (!this.map?.getSource(BAIRROS_SOURCE_ID)) return;

        if (!enabled) {
            this.map.getSource(BAIRROS_SOURCE_ID).setData(EMPTY_FC);
            return;
        }

        const cidades = this.formFiltersRef.current?.getFilters?.()?.cidades || [];
        if (cidades.length === 0) {
            toast.info('Selecione e busque uma cidade para ver os limites de bairros.');
        }
        this.renderBairros(cidades);
    };

    renderBairros = async (cidades) => {
        if (!this.map?.getSource(BAIRROS_SOURCE_ID)) return;

        const groups = await Promise.all((cidades || []).map((c) => this.fetchBairros(c.value)));
        const features = groups.flat();
        this.map.getSource(BAIRROS_SOURCE_ID)?.setData({ type: 'FeatureCollection', features });
    };

    // value = "UF-Cidade" (a UF vem primeiro; o nome da cidade pode conter '-', ex.: "SP-Embu-Guaçu").
    fetchBairros = async (value) => {
        if (this.bairrosCache[value]) return this.bairrosCache[value];
        const parsed = parseCidadeValue(value);
        if (!parsed) return [];

        try {
            const res = await axios.get(`/bairros/geojson/${encodeURIComponent(parsed.cidade)}/${encodeURIComponent(parsed.uf)}`);
            const features = (res.data && res.data.features) || [];
            this.bairrosCache[value] = features;
            return features;
        } catch (e) {
            console.error('[MapboxMap] erro ao carregar limites de bairros:', e);
            return [];
        }
    };

    // Verifica, a partir da lista de cidades com limites cadastrados (injetada no
    // page load via window.bairrosCities), se alguma cidade selecionada possui
    // limites de bairros. Atualiza bairrosAvailable (controla a exibição do switch).
    // Se nenhuma tiver, desliga e limpa eventuais limites já exibidos. Sem AJAX.
    refreshBairrosAvailability = (cidades) => {
        const available = (cidades || []).some((c) => BAIRROS_CITIES.has(c.value));
        this.setState({ bairrosAvailable: available });

        if (!available && this.state.showBairros) {
            this.setState({ showBairros: false });
            this.map?.getSource(BAIRROS_SOURCE_ID)?.setData(EMPTY_FC);
        }
        return available;
    };

    /**
     * "Sugerir Inclusão de Empreendimento" — no mapa Google o botão vive na navbar, sob a
     * rota 'mapa' (layouts/navbars/navs/auth.blade.php), layout que o mapa novo não usa.
     * Aqui aparece em dois lugares: no topo do painel de resultados e na aba Ferramentas
     * (a única sempre alcançável — o painel de resultados só abre depois de uma busca).
     * Mesmo destino de sempre: o formulário de chamado com o assunto pré-selecionado.
     *
     * @param {string} className Classe do call site (posicionamento/estilo).
     */
    renderSugerirInclusao = (className) => (
        <a className={className} href="/contato-infos?assunto=sugerir_inclusao" target="_blank" rel="noreferrer">
            <i className="fas fa-plus-circle mr-2" aria-hidden="true"></i>
            SUGERIR INCLUSÃO DE EMPREENDIMENTO
        </a>
    );

    render() {
        const { left_side_bar_visible, right_panel_visible, active_tab, totalEmpreendimentos, totalCarregados, cidadeSincronizada, searchAreas, showEmpreendimentoModal, projetos, loadedProjectId } = this.state;

        /*
         * Barras laterais fora do caminho enquanto um diálogo grande está aberto (ficha do
         * empreendimento, captura de tela, editores de cor da legenda, dicionário de dados,
         * listagem de polos da área).
         * É CSS, e não a prop `visible`: a Sidebar do PrimeReact desmonta o conteúdo ao
         * fechar, o que zeraria os filtros e a lista de formas a cada abertura. As aletas
         * somem junto — elas ficam em z-index 1000002, acima do mask do diálogo, e cobriam
         * a janela de edição.
         */
        const barrasOcultas = showEmpreendimentoModal
            || this.state.show_map_capture
            || this.state.show_modal_customizar_cores_setores
            || this.state.show_modal_data_dictionary
            || this.state.show_modal_datalayer_datatable
            || this.state.modalRangesOpen
            || Boolean(this.state.polosDaAreaModal);

        /*
         * O botão POLOS do card só aparece quando há polos na área, então a contagem é
         * derivada aqui em vez de guardada em estado — assim acompanha sozinha o
         * carregamento dos polos, o toggle e a edição das formas.
         */
        const areasComPolos = window.authenticatedUserCanQueryPolos
            ? searchAreas.map((a) => ({ ...a, totalPolos: this.polosDaArea(a).length }))
            : searchAreas;

        return (
            <>
                <div
                    ref={this.containerRef}
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                />

                <input
                    ref={this.searchInputRef}
                    type="text"
                    placeholder="Digite um endereço"
                    style={{
                        position: 'absolute',
                        top: 20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '40%',
                        minWidth: 280,
                        height: 40,
                        padding: '0 16px',
                        border: '1px solid #cccccc',
                        borderRadius: 4,
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                        fontSize: 14,
                        outline: 'none',
                        background: '#ffffff',
                        zIndex: 1,
                    }}
                />

                <LayerLoadingIndicator labels={this.state.layerLoading} />

                {/* Só faz sentido com o agrupamento ligado — é a escala de intensidade dos
                    clusters que ela explica. Mesma regra do mapa Google (use_cluster). */}
                {this.state.showPolosGeradores && this.state.clusterMarkers && (
                    <LegendaGradientePolos
                        categorias={this.state.polosCategoriasCarregadas}
                        sidebarAberta={left_side_bar_visible}
                    />
                )}

                {/* Aleta lateral: abre e fecha a barra, acompanhando a borda dela.
                    Substitui o "x" da Sidebar (showCloseIcon={false}) e o antigo botão
                    de reabrir. */}
                {!barrasOcultas && (
                    <button
                        type="button"
                        className={`mapbox-flap mapbox-flap-left${left_side_bar_visible ? ' is-open' : ''}`}
                        title={left_side_bar_visible ? 'Ocultar barra lateral' : 'Mostrar barra lateral'}
                        aria-expanded={left_side_bar_visible}
                        onClick={() => this.setState({ left_side_bar_visible: !left_side_bar_visible })}
                    >
                        <i className={`fas ${left_side_bar_visible ? 'fa-angle-double-left' : 'fa-angle-double-right'}`}></i>
                    </button>
                )}

                {/* `visible` fica sempre ligado: a Sidebar do PrimeReact desmonta o conteúdo
                    ao fechar (unmountOnExit), e com ele iriam os filtros já preenchidos —
                    que o mapa lê por ref (getFilters/getCidadesSelecionadas) para recortar
                    a busca da área desenhada e habilitar as camadas por cidade. Fechar é só
                    deslizar (.mapbox-sidebar-fechada). */}
                <Sidebar
                    visible
                    onHide={() => this.setState({ left_side_bar_visible: false })}
                    modal={false}
                    dismissable={false}
                    showCloseIcon={false}
                    className={`mapbox-sidebar${left_side_bar_visible ? '' : ' mapbox-sidebar-fechada'}${barrasOcultas ? ' mapbox-sidebar-oculta' : ''}`}
                >
                    <Tabs
                        activeKey={active_tab}
                        defaultActiveKey="filtros"
                        onSelect={(e) => this.setState({ active_tab: e })}
                        id="mapbox-tabs"
                    >
                        <Tab eventKey="filtros" title="Filtros">
                            <FormFiltersContainer
                                ref={this.formFiltersRef}
                                onSubmit={this.handleFiltersSubmit}
                                onClear={this.handleFiltersClear}
                                onEstadosChanged={this.centralizarMapaNaCapital}
                                onProjetoSelected={this.handleProjetoSelected}
                                projetos={projetos}
                                projeto={loadedProjectId}
                                onLoadProjetos={this.loadProjetos}
                                cidadeSincronizada={cidadeSincronizada}
                                showLegenda={totalCarregados > 0}
                            />
                        </Tab>

                        <Tab eventKey="ferramentas" title="Ferramentas">
                            <div style={{paddingRight: '6px', paddingLeft: '6px'}}>
                                <div className="card no-border-radius" style={{borderRadius: '0 !important'}}>
                                    <div className="card-body" style={{ height: '96vh', overflowY: 'auto', marginTop: '10px',marginBottom: '60px'}}>
                                        {/* Todos os switches juntos numa seção única (antes cada um
                                    tinha um form-group próprio separado por <hr/>) */}
                                        {/* Antes era um switch "Modo noturno"; com quatro estilos
                                            virou lista. A preferência segue salva no navegador. */}
                                        <div className="form-group" title="A preferência fica salva neste navegador.">
                                            <label className="mb-2 d-block">Mapa base</label>
                                            <Dropdown
                                                value={this.state.mapStyle}
                                                onChange={(e) => this.setMapStyle(e.value)}
                                                options={MAP_STYLE_OPTIONS}
                                                optionLabel="label"
                                                appendTo={document.body}
                                                style={{ width: '100%' }}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label className="mb-2 d-block">Exibir no mapa</label>
                                            <div className="mapbox-tools-switches">
                                                <div className="d-flex align-items-center">
                                                    <InputSwitch
                                                        inputId="check_empreendimentos"
                                                        checked={this.state.showEmpreendimentos}
                                                        onChange={(e) => this.toggleEmpreendimentos(e.value)}
                                                    />
                                                    <label htmlFor="check_empreendimentos" className="pl-1 mb-0 ms-2">
                                                        Empreendimentos
                                                    </label>
                                                </div>

                                                <div
                                                    className="d-flex align-items-center"
                                                    title="Desligado, cada empreendimento e cada polo gerador aparece como um marcador próprio, sem agrupar por proximidade."
                                                >
                                                    <InputSwitch
                                                        inputId="check_cluster_marcadores"
                                                        checked={this.state.clusterMarkers}
                                                        onChange={(e) => this.toggleClusterMarkers(e.value)}
                                                    />
                                                    <label htmlFor="check_cluster_marcadores" className="pl-1 mb-0 ms-2">
                                                        Agrupar marcadores
                                                    </label>
                                                </div>

                                                {window.isCliente === false && (
                                                    <div className="d-flex align-items-center">
                                                        <InputSwitch
                                                            inputId="check_incluir_monitoradas"
                                                            checked={this.state.incluirNaoMonitoradas}
                                                            onChange={(e) => this.setState({ incluirNaoMonitoradas: e.value })}
                                                        />
                                                        <label htmlFor="check_incluir_monitoradas" className="pl-1 mb-0 ms-2">
                                                            Incluir cidades não monitoradas
                                                        </label>
                                                    </div>
                                                )}

                                                {this.state.bairrosAvailable && (
                                                    <div className="d-flex align-items-center">
                                                        <InputSwitch
                                                            inputId="check_bairros"
                                                            checked={this.state.showBairros}
                                                            onChange={(e) => this.handleToggleBairros(e.value)}
                                                        />
                                                        <label htmlFor="check_bairros" className="pl-1 mb-0 ms-2">
                                                            Limite de bairros
                                                        </label>
                                                    </div>
                                                )}

                                                <div className="d-flex align-items-center">
                                                    <InputSwitch
                                                        inputId="check_polos_geradores"
                                                        checked={this.state.showPolosGeradores}
                                                        onChange={(e) => this.togglePolos(e.value)}
                                                    />
                                                    <label htmlFor="check_polos_geradores" className="pl-1 mb-0 ms-2">
                                                        Polos geradores
                                                    </label>
                                                    {/* Uma cidade grande traz dezenas de milhares de polos e a busca leva
                                                        dezenas de segundos na primeira vez (depois vem do cache). */}
                                                    {this.state.polosLoading && (
                                                        <i className="pi pi-spin pi-spinner ml-2" title="Carregando polos geradores…" />
                                                    )}
                                                    {this.state.showPolosGeradores && !this.state.polosLoading && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-link p-0 ml-2"
                                                            onClick={() => this.setState({ polosModalOpen: true })}
                                                            title="Selecionar categorias de polos"
                                                        >
                                                            {/* Sem categoria nada foi carregado ainda — o rótulo é a
                                                                chamada para o passo que falta. */}
                                                            {this.state.polosTypesSelected.length > 0
                                                                ? `Categorias (${this.state.polosTypesSelected.length})`
                                                                : 'Selecionar categorias'}
                                                        </button>
                                                    )}
                                                </div>

                                                {CAN_VER_ZONEAMENTO && this.state.zoneamentoAvailable && (
                                                    <>
                                                        <div className="d-flex align-items-center">
                                                            <InputSwitch
                                                                inputId="check_zoneamento"
                                                                checked={this.state.showZoneamento}
                                                                onChange={(e) => this.toggleShowZoneamento(e.value)}
                                                            />
                                                            <label htmlFor="check_zoneamento" className="pl-1 mb-0 ms-2">
                                                                Zoneamento
                                                            </label>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {this.state.drawnShapes.length > 0 && (
                                            <div>
                                                <hr />
                                                <DrawnShapesList
                                                    shapes={this.state.drawnShapes}
                                                    onCenter={this.centerOnShape}
                                                    onToggleVisibility={this.toggleShapeVisibility}
                                                    onSetRadius={this.setShapeRadius}
                                                    onEditAttributes={this.editShapeAttributes}
                                                    onRemove={this.removeShape}
                                                />
                                            </div>
                                        )}

                                        <SociodemografiaTools
                                            permissoes={this.state.permissoesSociodemografia}
                                            datalayers={this.state.datalayers}
                                            datalayerName={this.state.datalayerName}
                                            datalayerColorProperty={this.state.datalayerColorProperty}
                                            datalayerColorPropertyOptions={this.state.datalayerColorPropertyOptions}
                                            datalayerYear={this.state.datalayerYear}
                                            datalayerYears={this.state.datalayerYears}
                                            datalayerYearOptions={this.state.datalayerYearOptions}
                                            showDemarcacaoAreas={this.state.show_demarcacao_areas}
                                            loading={this.state.loadingDatalayer}
                                            requerPropriedade={this.camadaRequerPropriedadeParaColorirMapa()}
                                            requerAno={this.camadaRequerAno()}
                                            requerMultiplosAnos={this.camadaRequerMultiplosAnos()}
                                            onChangeName={this.onChangeDatalayerName}
                                            onChangeColorProperty={this.onChangeDatalayerColorProperty}
                                            onChangeYear={this.onChangeDatalayerYear}
                                            onChangeYears={this.onChangeDatalayerYears}
                                            onLoadDataLayer={this.loadDataLayer}
                                            onChangeDemarcacaoAreas={this.setDemarcacaoAreas}
                                            onOpenDataDictionary={() => this.setState({ show_modal_data_dictionary: true })}
                                            onOpenDataTable={this.loadDataLayerAggregated}
                                        />

                                        <hr />

                                        {/* Todos os botões de ação juntos num único bloco
                                            (antes cada um tinha um form-group próprio separado por <hr/>) */}
                                        <div className="form-group">
                                            <div className="mapbox-tools-actions">

                                                {this.state.showHeatMap ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger"
                                                        onClick={this.removeHeatMap}
                                                    >
                                                        Remover Calor
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-default bold rem10"
                                                        onClick={() => this.setState({ modalHeatMapOpen: true })}
                                                        disabled={totalEmpreendimentos === 0}
                                                        title={totalEmpreendimentos === 0 ? 'Faça uma busca para habilitar' : 'Criar mapa de calor'}
                                                    >
                                                        Mapa de Calor
                                                    </button>
                                                )}

                                                <button
                                                    type="button"
                                                    className="btn btn-default bold rem10"
                                                    onClick={() => this.setState({ modalRangesOpen: true })}
                                                    title="Define cores/imagens de marcador por faixas de área, preço, oferta ou padrão"
                                                >
                                                    Personalizar Marcadores
                                                    {this.state.ranges.length > 0 && ` (${this.state.ranges.length})`}
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-default bold rem10"
                                                    onClick={this.handleDownloadKml}
                                                    disabled={this.state.loadingKml}
                                                    title="Exporta formas desenhadas e empreendimentos como KML"
                                                >
                                                    Download KML
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-default bold rem10"
                                                    onClick={() => this.kmlInputRef.current?.click()}
                                                    disabled={this.state.loadingKml}
                                                    title="Importa polígonos de um arquivo KML ou KMZ"
                                                >
                                                    Importar KMZ
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-default btn-block"
                                                    onClick={this.printMap}
                                                    title="Efetua Captura de Tela do Mapa"
                                                >
                                                    Capturar
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-default btn-block"
                                                    onClick={() => this.setState({ modalSalvarProjetoOpen: true })}
                                                    title="Salva filtros, formas e camadas atuais como um projeto (com compartilhamento)"
                                                >
                                                    Salvar Projeto
                                                </button>

                                                {this.state.loadedProjectId && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger"
                                                        onClick={this.deleteProject}
                                                        title="Exclui o projeto atualmente carregado"
                                                    >
                                                        Excluir Projeto
                                                    </button>
                                                )}
                                            </div>

                                            {/* Ajuste ao vivo, com o mapa à vista — o modal de criação cobre
                                                justamente o que se quer avaliar ao mexer no raio. */}
                                            {this.state.showHeatMap && (
                                                <div className="mt-2">
                                                    <HeatMapControls
                                                        compact
                                                        style={this.state.heatMapStyle}
                                                        onChange={this.updateHeatStyle}
                                                    />
                                                </div>
                                            )}

                                            <input
                                                ref={this.kmlInputRef}
                                                type="file"
                                                accept=".kml,.kmz"
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    this.handleImportKmlFile(e.target.files?.[0]);
                                                    e.target.value = '';
                                                }}
                                            />
                                            {this.state.loadingKml && (
                                                <small className="text-muted d-block mt-2">Processando arquivo…</small>
                                            )}
                                            <div style={{marginBottom: '50px'}}>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </Tab>

                        <Tab eventKey="legendas" title="Legenda">

                            <div style={{paddingRight: '6px', paddingLeft: '6px'}}>
                                <div className="card no-border-radius" style={{borderRadius: '0 !important'}}>
                                    <div
                                        className="card-body"
                                        style={{ height: '96vh', overflowY: 'auto', marginTop: '10px' }}
                                        id="legendas-container"
                                    >
                                        {this.state.showZoneamento && this.state.legendaZoneamento.length > 0 && (
                                            <LegendaDataLayer legendas={this.state.legendaZoneamento} />
                                        )}
                                        {this.state.showLegendaRenda && (
                                            <RendaIbgeLegend
                                                openModalLegendas={this.openModalLegendaSetores}
                                                colorRanges={this.state.color_ranges[this.getColorRangeKey()]}
                                                title={this.state.datalayerFriendlyName}
                                                datalayerName={this.state.datalayerName}
                                                datalayerColorProperty={this.state.datalayerColorProperty}
                                            />
                                        )}
                                        {this.state.showPolosGeradores && this.state.polosLoaded ? (
                                            <LegendaPoloGerador />
                                        ) : this.state.showHeatMap ? (
                                            <HeatMapLegend
                                                weightType={this.state.heatMapWeightType}
                                                config={this.state.heatMapLegendConfig}
                                            />
                                        ) : totalCarregados > 0 ? (
                                            <Legenda ranges={this.state.ranges} />
                                        ) : !this.state.showZoneamento && !this.state.showLegendaRenda ? (
                                            <p className="text-muted small">
                                                Faça uma busca para visualizar a legenda.
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            </div>


                        </Tab>
                    </Tabs>
                </Sidebar>

                {searchAreas.length > 0 && !barrasOcultas && (
                    <button
                        type="button"
                        className={`mapbox-flap mapbox-flap-right${right_panel_visible ? ' is-open' : ''}`}
                        title={right_panel_visible ? 'Ocultar resultados' : 'Mostrar resultados'}
                        aria-expanded={right_panel_visible}
                        onClick={() => this.setState({ right_panel_visible: !right_panel_visible })}
                    >
                        <i className={`fas ${right_panel_visible ? 'fa-angle-double-right' : 'fa-chart-bar'}`}></i>
                        {!right_panel_visible && (
                            <span className="mapbox-flap-badge">{searchAreas.length}</span>
                        )}
                    </button>
                )}

                {/* A largura vem do CSS (--mapbox-right-panel-width) para a aleta poder
                    acompanhar a borda do painel. */}
                <Sidebar
                    visible={right_panel_visible}
                    position="right"
                    onHide={() => this.setState({ right_panel_visible: false })}
                    modal={false}
                    dismissable={false}
                    showCloseIcon={false}
                    className={`mapbox-right-panel${barrasOcultas ? ' mapbox-sidebar-oculta' : ''}`}
                >
                    {this.renderSugerirInclusao('mapbox-sugerir-inclusao mapbox-sugerir-inclusao-painel')}

                    <div className="mapbox-right-panel-header">
                        <strong>Resultados</strong>
                        <span className="badge badge-secondary ml-2">{searchAreas.length}</span>
                    </div>
                    <div className="mapbox-right-panel-body">
                        <MapboxResultsPanel
                            searchAreas={areasComPolos}
                            onRemoveArea={this.handleRemoveArea}
                            onCompareAreas={
                                this.compareAreasOptions().length >= 2
                                    ? () => this.setState({ showCompareAreasModal: true })
                                    : null
                            }
                            onComparePolos={
                                this.state.polosLoaded && this.allPolos.length > 0 && this.state.drawnShapes.length >= 1
                                    ? () => this.setState({ showComparePolosModal: true })
                                    : null
                            }
                            onVerPolos={this.abrirPolosDaArea}
                            onAbrirDashboard={(url) => this.setState({ dashboardUrl: url })}
                        />
                    </div>
                </Sidebar>

                <CompareModal
                    title="Comparar Áreas"
                    visible={this.state.showCompareAreasModal}
                    onHide={() => this.setState({ showCompareAreasModal: false })}
                    options={this.state.showCompareAreasModal ? this.compareAreasOptions() : []}
                    onCompare={this.handleCompareAreas}
                />

                <CompareModal
                    title="Comparar Polos"
                    visible={this.state.showComparePolosModal}
                    onHide={() => this.setState({ showComparePolosModal: false })}
                    options={this.state.showComparePolosModal ? this.comparePolosOptions() : []}
                    onCompare={this.handleComparePolos}
                />

                {this.state.empreendimentoDetail && (
                    <EmpreendimentoModal
                        show={this.state.showEmpreendimentoModal}
                        empreendimento={this.state.empreendimentoDetail}
                        closeModalEmpreendimento={this.closeEmpreendimentoModal}
                        togglePainelEmpreendimento={this.closeEmpreendimentoModal}
                    />
                )}

                <PolosDaAreaModal
                    show={Boolean(this.state.polosDaAreaModal)}
                    onHide={() => this.setState({ polosDaAreaModal: null })}
                    area={this.state.polosDaAreaModal?.area || null}
                    polos={this.state.polosDaAreaModal?.polos || []}
                    onExport={this.exportarPolosDaArea}
                />

                {/* Modal legado reusado sem alteração: recebe a URL do POST e baixa o
                    PDF. `originalState` é o endpoint — nome herdado do mapa Google. */}
                <ModalDashboard
                    show={Boolean(this.state.dashboardUrl)}
                    closeModal={() => this.setState({ dashboardUrl: null })}
                    setLoading={(gerandoDashboard) => this.setState({ gerandoDashboard })}
                    originalState={this.state.dashboardUrl}
                />

                {this.state.gerandoDashboard && (
                    <div className="mapbox-layer-loading" role="status">
                        <i className="fa fa-spin fa-spinner"></i>
                        Gerando dashboard…
                    </div>
                )}

                <MapCapture
                    visible={this.state.show_map_capture}
                    handleClose={this.closeMapCapture}
                    imageData={this.state.mapCaptureData}
                    canvas={this.state.mapCaptureCanvas}
                />

                <HeatMapModal
                    show={this.state.modalHeatMapOpen}
                    closeModal={() => this.setState({ modalHeatMapOpen: false })}
                    addHeatMap={this.addHeatMap}
                    styleInicial={this.state.heatMapStyle}
                />

                <ShapeAttributesEditor
                    show={this.state.shapeEditorOpen}
                    shape={this.state.shapeEditing}
                    onHide={this.closeShapeEditor}
                    onApply={(attrs) => this.applyShapeAttributes(this.state.shapeEditing, attrs)}
                />

                <PolosGeradoresTypesModal
                    show={this.state.polosModalOpen}
                    closeModal={() => this.setState({ polosModalOpen: false })}
                    onApply={this.applyPolosTypes}
                    selected={this.state.polosTypesSelected}
                />

                <RaioDeslocamentoModal
                    show={this.state.showRaioDeslocamento}
                    center={this.state.isochroneCenter}
                    onHide={() => this.setState({ showRaioDeslocamento: false })}
                    onIsochronesReady={this.handleIsochronesReady}
                />

                <ModalRanges
                    show={this.state.modalRangesOpen}
                    ranges={this.state.ranges}
                    closeModal={() => this.setState({ modalRangesOpen: false })}
                    addRange={this.addRange}
                    updateRange={this.updateRange}
                    removeRange={this.removeRange}
                    replaceMarkers={this.applyMarkerRanges}
                />

                <ModalSalvarProjeto
                    show={this.state.modalSalvarProjetoOpen}
                    handleClose={() => this.setState({ modalSalvarProjetoOpen: false, projectErrors: {} })}
                    project_name={this.state.projectName}
                    handleNameProjectChanged={(e) => this.setState({ projectName: e.target.value })}
                    share_users={this.state.projectShareUsers}
                    handleShareUsersChanged={(users) => this.setState({ projectShareUsers: users || [] })}
                    errors={this.state.projectErrors}
                    loading={this.state.savingProject}
                    saveProject={this.saveProject}
                />

                <LinkagesDataDictionaryModal
                    visible={this.state.show_modal_data_dictionary}
                    onHide={() => this.setState({ show_modal_data_dictionary: false })}
                    activeDatalayer={this.state.datalayerName}
                />

                {this.state.show_modal_datalayer_datatable && (
                    <LinkagesDataTableModal
                        visible={this.state.show_modal_datalayer_datatable}
                        onHide={this.closeModalDatalayerTable}
                        activeDatalayer={this.state.datalayerName}
                        overlays={this.overlaysForDatalayerTable()}
                        data={this.state.datalayers_table_data}
                        getTransformedPayload={() => this.buildSensoPayload()}
                    />
                )}

                <ModalLegendaRendaIbge
                    show={this.state.show_modal_customizar_cores_setores}
                    handleClose={this.closeModalLegendaSetores}
                    colorRanges={this.state.color_ranges[this.getColorRangeKey()]}
                    updateRange={this.updateRendaColorRange}
                    replaceRanges={this.replaceColorRanges}
                    removeRange={this.removeRendaColorRange}
                    handleAplyColorRanges={this.handleAplyColorRanges}
                    colorOptions={this.state.colorsOptions}
                    datalayerName={this.state.datalayerFriendlyName}
                    datalayerColorProperty={this.state.datalayerColorProperty}
                    datalayerFillOpacity={this.state.datalayerFillOpacity}
                    onChangeOpacidadePreenchimento={this.onChangeOpacidadePreenchimento}
                />

                <Toast ref={this.toastRef} position="top-right" />
            </>
        );
    }
}

export default MapboxMap;

if (document.getElementById('mapbox-map')) {
    ReactDOM.render(<MapboxMap />, document.getElementById('mapbox-map'));
}
